/**
 * Packaging OS — i18n strings (Phase 1N-B / 1O-A / 1P-A merged).
 *
 * Production-quality strings for the four real screens shipping in this
 * phase (Public Landing / Login / Preview / App Shell) plus shared
 * UI primitives (Trust banner, Status pill, Buttons, Errors).
 *
 * Hard rules (visual-do-dont.md):
 *   - No Lorem ipsum.
 *   - No "TODO" placeholder strings visible to the user.
 *   - No transliteration as a substitute for translation.
 *   - No fake claims, no fake testimonials, no fake metrics.
 *   - No AI hype copy.
 *   - Every string in the four supported locales (en / ar / fa / tr).
 *   - Italic forbidden in ar/fa (handled by base.css).
 *
 * Future phases will expand this dictionary. The shape is flat to keep
 * the lookup signature simple — `strings(locale).<key>`.
 */

import type { LocaleCode } from "../locales";

export interface UiStrings {
  // Common
  appName: string;
  switchLanguageLabel: string;
  copyrightLine: string;

  // Trust
  trustBannerFull: string;
  trustBannerShort: string;
  trustNotesSourceBacked: string;
  trustNotesRegulatoryGuarantee: string;
  trustNotesFinalOutput: string;

  // Buttons / CTAs
  ctaSignInWithGoogle: string;
  ctaRequestAccess: string;
  ctaOpenDashboard: string;
  ctaSignOut: string;
  ctaBackToLanding: string;

  // Public Landing
  landingHeroTitle: string;
  landingHeroSubtitle: string;
  landingTrustStripTitle: string;
  landingTrustStripDraftItem: string;
  landingTrustStripNoLiveAiItem: string;
  landingTrustStripControlledAccessItem: string;
  landingCapabilityHeading: string;
  landingCapabilityCatalogTitle: string;
  landingCapabilityCatalogBody: string;
  landingCapabilityWorkflowsTitle: string;
  landingCapabilityWorkflowsBody: string;
  landingCapabilityTrustTitle: string;
  landingCapabilityTrustBody: string;
  landingWorkflowPreviewHeading: string;
  landingWorkflowPreviewBody: string;
  landingAccessHeading: string;
  landingAccessBody: string;
  landingCtaSectionHeading: string;
  landingCtaSectionBody: string;
  landingFooterEcosystem: string;

  // Login
  loginTitle: string;
  loginGoogleHelperText: string;
  loginEmailComingSoonLabel: string;
  loginEmailComingSoonTooltip: string;
  loginRequestAccessLead: string;
  loginTrustParagraph: string;
  loginCoreUrlMissingNotice: string;
  // MM2 — internal email/password auth
  loginEmailLabel: string;
  loginPasswordLabel: string;
  loginSubmitLabel: string;
  loginSubmittingLabel: string;
  loginErrorInvalidCredentials: string;
  loginErrorSuspended: string;
  loginErrorUnavailable: string;
  loginGoogleUnavailableNotice: string;
  loginPreviewModeNotice: string;
  loginPreviewContinue: string;
  loginErrorOauthCancelled: string;
  loginErrorOauthFailed: string;
  loginErrorServiceDown: string;
  loginPostauthChecking: string;
  loginPostauthSessionMissing: string;
  loginPostauthAccessDenied: string;
  loginPostauthUnavailable: string;
  loginPostauthContinue: string;

  // Preview / Request access
  previewTitle: string;
  previewBanner: string;
  previewYouCanHeading: string;
  previewYouCanItem1: string;
  previewYouCanItem2: string;
  previewLockedHeading: string;
  previewLockedAuditReason: string;
  previewLockedExportReason: string;
  previewLockedReviewReason: string;
  previewLockedSupplierReason: string;
  previewContactAdminLead: string;
  previewRequestAccessNotice: string;

  // App shell
  appShellTitle: string;
  appShellSubtitle: string;
  appShellWorkspacePending: string;
  appShellApiStatusHeading: string;
  appShellNavCatalog: string;
  appShellNavWorkflows: string;
  appShellNavTrust: string;
  appShellNavSettings: string;
  appShellSectionPlaceholder: string;
  appShellComingSoonBrands: string;
  appShellComingSoonProducts: string;
  appShellComingSoonProjects: string;
  appShellComingSoonAudit: string;

  // Whoami / api states
  whoAmIChecking: string;
  whoAmIOkLead: string;
  whoAmIRoles: string;
  whoAmIPermissionsCount: string;
  whoAmIAccessModeLabel: string;
  whoAmIPreviewLabel: string;
  accessModeSuperAdmin: string;
  accessModeAdmin: string;
  accessModeOperator: string;
  accessModeUser: string;
  accessModePreview: string;
  accessModeUnknown: string;

  // Generic API errors
  errAuthMissing: string;
  errAuthInvalid: string;
  errPermissionDenied: string;
  errRateLimited: string;
  errServiceUnavailable: string;
  errNetwork: string;
  errUnknown: string;
  errRequestIdLabel: string;

  // Not found
  notFoundTitle: string;
  notFoundBody: string;

  // Catalog (Phase 1O-B)
  catalogBrandsTitle: string;
  catalogProductsTitle: string;
  catalogProjectsTitle: string;
  catalogColumnName: string;
  catalogColumnSlug: string;
  catalogColumnVersion: string;
  catalogColumnUpdated: string;
  catalogColumnCategory: string;
  catalogColumnStatus: string;
  catalogColumnMarkets: string;
  catalogColumnLanguages: string;
  catalogEmptyTitle: string;
  catalogEmptyBody: string;
  catalogPreviewRestrictedTitle: string;
  catalogPreviewRestrictedBody: string;
  catalogPreviewRestrictedCta: string;
  catalogPaginationLabel: string;
  catalogPrevPage: string;
  catalogNextPage: string;
  catalogReadOnlyNotice: string;

  // Dry-run workflow form (Phase 1P-B)
  workflowDryRunTitle: string;
  workflowDryRunSubtitle: string;
  workflowDryRunFieldProductName: string;
  workflowDryRunFieldProductCategory: string;
  workflowDryRunFieldMarket: string;
  workflowDryRunFieldChannel: string;
  workflowDryRunFieldPackageType: string;
  workflowDryRunFieldMaterial: string;
  workflowDryRunFieldClaims: string;
  workflowDryRunFieldClaimsHelp: string;
  workflowDryRunFieldTargetCustomer: string;
  workflowDryRunFieldLanguage: string;
  workflowDryRunFieldNotes: string;
  workflowDryRunSubmit: string;
  workflowDryRunSubmitting: string;
  workflowDryRunReset: string;
  workflowDryRunRequiredHint: string;
  workflowDryRunNotSelected: string;
  workflowDryRunValidationFailed: string;

  // Phase Core/AI — AI-assist toggle + status banners
  aiAssistToggleLabel: string;
  aiAssistToggleHint: string;
  aiAssistDraftBanner: string;
  aiAssistStatusOk: string;
  aiAssistStatusFallback: string;
  aiAssistStatusOff: string;
  aiAssistAuditHeader: string;

  // Dry-run report (Phase 1P-B)
  reportInputSummary: string;
  reportPackagingAudit: string;
  reportPackagingAuditPositioning: string;
  reportPackagingAuditPackStructure: string;
  reportPackagingAuditShelf: string;
  reportLabelCopy: string;
  reportLabelCopyFront: string;
  reportLabelCopySide: string;
  reportLabelCopyTone: string;
  reportMaterialNotes: string;
  reportClaimRisk: string;
  reportClaimRiskLevelLabel: string;
  reportClaimRiskFlags: string;
  reportClaimRiskReviewRequired: string;
  reportClaimRiskReviewReasons: string;
  reportSupplierBrief: string;
  reportLaunchKit: string;
  reportTrustNotes: string;
  reportNextSteps: string;
  reportRiskLow: string;
  reportRiskMedium: string;
  reportRiskHigh: string;
  reportReviewRequiredYes: string;
  reportReviewRequiredNo: string;
  reportHaltBannerHigh: string;
  reportHaltBannerMedium: string;
  reportNoFlags: string;

  // Phase 22A — Product Brief + Brand Intake
  intakeTitle: string;
  intakeSubtitle: string;
  intakeGroupProduct: string;
  intakeGroupBrand: string;
  intakeGroupMarket: string;
  intakeGroupObjective: string;
  intakeGroupConstraints: string;
  intakeGroupAssets: string;
  intakeFieldProductName: string;
  intakeFieldProductCategory: string;
  intakeFieldProductSubcategory: string;
  intakeFieldProductDescription: string;
  intakeFieldProductForm: string;
  intakeFieldSizeValue: string;
  intakeFieldSizeUnit: string;
  intakeFieldPriceBand: string;
  intakeFieldBrandName: string;
  intakeFieldBrandPersonality: string;
  intakeFieldBrandMaturity: string;
  intakeFieldCurrentPackagingStatus: string;
  intakeFieldVisualPreferences: string;
  intakeFieldToneOfVoice: string;
  intakeFieldTargetMarkets: string;
  intakeFieldTargetMarketsHelp: string;
  intakeFieldTargetLanguages: string;
  intakeFieldSalesChannels: string;
  intakeFieldTargetAudience: string;
  intakeFieldObjectives: string;
  intakeFieldObjectivesHelp: string;
  intakeFieldMaterialConstraints: string;
  intakeFieldBudgetBand: string;
  intakeFieldProductionConstraints: string;
  intakeFieldSustainabilityPreferences: string;
  intakeFieldSustainabilityPreferencesHelp: string;
  intakeFieldClaimsToAvoid: string;
  intakeFieldClaimsToAvoidHelp: string;
  intakeFieldAssetsAvailable: string;
  intakeFieldLanguage: string;
  intakeFieldNotes: string;
  intakeSubmit: string;
  intakeSubmitting: string;
  intakeReset: string;
  intakeReportProductBrief: string;
  intakeReportBrandContext: string;
  intakeReportObjectiveSummary: string;
  intakeReportMarketChannelFit: string;
  intakeReportMissingChecklist: string;
  intakeReportAssumptionLog: string;
  intakeReportInitialRiskFlags: string;
  intakeReportHumanReview: string;
  intakeReportRecommendedNextEngines: string;

  // Phase 22B deepening — section headings only
  intakeReportStrategy: string;
  intakeReportTargetCustomer: string;
  intakeReportPositioning: string;
  intakeReportChannelReadiness: string;
  intakeReportLocalization: string;
  intakeReportDecisionPriorities: string;
  intakeReportOwnerQuestions: string;
  intakeNextLabelCopy: string;

  // Phase 23A — Label / Copy + Claim Risk
  labelCopyTitle: string;
  labelCopySubtitle: string;
  labelCopyFieldProductName: string;
  labelCopyFieldCategory: string;
  labelCopyFieldBrandName: string;
  labelCopyFieldTargetMarket: string;
  labelCopyFieldTargetLanguage: string;
  labelCopyFieldSalesChannel: string;
  labelCopyFieldProductDescription: string;
  labelCopyFieldPackagingObjective: string;
  labelCopyFieldClaimDrafts: string;
  labelCopyFieldClaimDraftsHelp: string;
  labelCopyFieldToneOfVoice: string;
  labelCopyFieldClaimsToAvoid: string;
  labelCopyFieldRequiredLabelElements: string;
  labelCopyFieldRequiredLabelElementsHelp: string;
  labelCopySubmit: string;
  labelCopySubmitting: string;
  labelCopyReset: string;
  labelCopyReportFront: string;
  labelCopyReportBack: string;
  labelCopyReportStory: string;
  labelCopyReportInstructions: string;
  labelCopyReportWarning: string;
  labelCopyReportClaimRiskTable: string;
  labelCopyReportSaferWording: string;
  labelCopyReportMissingInfo: string;
  labelCopyReportHumanReview: string;
  labelCopyReportCopyQuality: string;
  labelCopyClaimColClaim: string;
  labelCopyClaimColSeverity: string;
  labelCopyClaimColCategory: string;
  labelCopyClaimColMatched: string;
  labelCopyClaimColReview: string;

  // Phase 24 — Material / Structure
  materialTitle: string;
  materialSubtitle: string;
  materialFieldFragility: string;
  materialFieldTemperatureSensitive: string;
  materialFieldSustainabilityPreferences: string;
  materialReportFormat: string;
  materialReportMaterial: string;
  materialReportStructure: string;
  materialReportDurability: string;
  materialReportDelivery: string;
  materialReportEcommerce: string;
  materialReportShelf: string;
  materialReportProduction: string;
  materialReportCost: string;
  materialReportSupplierQuestions: string;
  materialSubmit: string;
  materialSubmitting: string;
  materialReset: string;
  intakeNextMaterial: string;

  // Phase 25A — Sustainability / Risk
  sustTitle: string;
  sustSubtitle: string;
  sustFieldPackagingMaterial: string;
  sustFieldClaims: string;
  sustFieldClaimsHelp: string;
  sustFieldLabelDrafts: string;
  sustFieldRecyclable: string;
  sustFieldBiodegradable: string;
  sustFieldCompostable: string;
  sustFieldOrigin: string;
  sustFieldBarcode: string;
  sustFieldExpiry: string;
  sustFieldOriginLabel: string;
  sustFieldWarning: string;
  sustReportClaimReview: string;
  sustReportGreenwashing: string;
  sustReportSeverityTable: string;
  sustReportSafer: string;
  sustReportCompliance: string;
  sustReportCountry: string;
  sustReportEvidence: string;
  sustReportNotApproved: string;
  sustSubmit: string;
  sustSubmitting: string;
  sustReset: string;
  sustColTerm: string;
  sustColSeverity: string;
  sustColCategory: string;
  sustColEvidence: string;
  labelCopyNextSust: string;

  // Phase 26 — Supplier / RFQ
  supplierTitle: string;
  supplierSubtitle: string;
  supplierFieldFormat: string;
  supplierFieldMaterial: string;
  supplierFieldStructure: string;
  supplierFieldQuantity: string;
  supplierFieldPrintFinish: string;
  supplierFieldKnownConstraints: string;
  supplierFieldQuestions: string;
  supplierFieldQuestionsHelp: string;
  supplierReportBrief: string;
  supplierReportSpec: string;
  supplierReportMaterial: string;
  supplierReportPrint: string;
  supplierReportQuantity: string;
  supplierReportLead: string;
  supplierReportQuality: string;
  supplierReportSust: string;
  supplierReportRfqEmail: string;
  supplierReportCompare: string;
  supplierReportMissing: string;
  supplierSubmit: string;
  supplierSubmitting: string;
  supplierReset: string;
  materialNextSupplier: string;
  sustNextSupplier: string;

  // Phase 27A — Visual Direction
  visualTitle: string;
  visualSubtitle: string;
  visualFieldBrandPersonality: string;
  visualFieldTargetCustomer: string;
  visualFieldVisualPreference: string;
  visualFieldColorsToUse: string;
  visualFieldColorsToAvoid: string;
  visualFieldTypographyPreference: string;
  visualFieldCompetitorReferences: string;
  visualFieldCompetitorReferencesHelp: string;
  visualReportSummary: string;
  visualReportRoutes: string;
  visualReportColor: string;
  visualReportTypography: string;
  visualReportLayout: string;
  visualReportSurface: string;
  visualReportShelfEc: string;
  visualReportCultural: string;
  visualReportMockup: string;
  visualReportHandoff: string;
  visualReportRisks: string;
  visualSubmit: string;
  visualSubmitting: string;
  visualReset: string;
  intakeNextVisual: string;
  labelCopyNextVisual: string;

  // Phase 28 + 29A — Packaging Scorecard + Vertical Packs
  scorecardTitle: string;
  scorecardSubtitle: string;
  scorecardFieldVerticalPack: string;
  scorecardFieldShelfEnv: string;
  scorecardFieldFragility: string;
  scorecardFieldLiquid: string;
  scorecardFieldTempSensitive: string;
  scorecardFieldThumbnailOpt: string;
  scorecardFieldProtectiveInsert: string;
  scorecardFieldLeakSeal: string;
  scorecardFieldDropTest: string;
  scorecardFieldVisualNotes: string;
  scorecardOverall: string;
  scorecardSectionShelf: string;
  scorecardSectionEcommerce: string;
  scorecardSectionDelivery: string;
  scorecardSectionUnboxing: string;
  scorecardSectionMarketplace: string;
  scorecardVerticalPackLabel: string;
  scorecardRiskFlags: string;
  scorecardImprovements: string;
  scorecardChannelRecs: string;
  scorecardMissing: string;
  scorecardSubmit: string;
  scorecardSubmitting: string;
  scorecardReset: string;
  scorecardBandHigh: string;
  scorecardBandMedium: string;
  scorecardBandLow: string;
  visualNextScorecard: string;
  supplierNextScorecard: string;

  // Phase 32 — Launch Kit
  launchKitTitle: string;
  launchKitSubtitle: string;
  launchKitFieldPositioning: string;
  launchKitFieldKeyBenefit: string;
  launchKitFieldClaimRestrictions: string;
  launchKitFieldQrConnected: string;
  launchKitFieldLaunchChannels: string;
  launchKitReportPositioning: string;
  launchKitReportListing: string;
  launchKitReportSocial: string;
  launchKitReportInsert: string;
  launchKitReportQr: string;
  launchKitReportLoyalty: string;
  launchKitReportSafety: string;
  launchKitSubmit: string;
  launchKitSubmitting: string;
  launchKitReset: string;

  // Phase 33 — Post-Launch Analysis
  postLaunchTitle: string;
  postLaunchSubtitle: string;
  postLaunchFieldReviews: string;
  postLaunchFieldReturns: string;
  postLaunchFieldFeedback: string;
  postLaunchFieldPerformance: string;
  postLaunchFieldVersion: string;
  postLaunchReportThemes: string;
  postLaunchReportComplaints: string;
  postLaunchReportReturns: string;
  postLaunchReportSentiment: string;
  postLaunchReportImprovements: string;
  postLaunchReportNext: string;
  postLaunchReportDataQuality: string;
  postLaunchSubmit: string;
  postLaunchSubmitting: string;
  postLaunchReset: string;

  // Phase 34 — Review Checklist
  reviewChecklistTitle: string;
  reviewChecklistSubtitle: string;
  reviewChecklistFieldTarget: string;
  reviewChecklistFieldHighRisk: string;
  reviewChecklistFieldEvidenceGap: string;
  reviewChecklistFieldSustClaim: string;
  reviewChecklistFieldFoodContact: string;
  reviewChecklistFieldCosmetic: string;
  reviewChecklistFieldIndustrial: string;
  reviewChecklistFieldSupplier: string;
  reviewChecklistReportChecklist: string;
  reviewChecklistReportCriteria: string;
  reviewChecklistReportRisks: string;
  reviewChecklistReportEvidence: string;
  reviewChecklistReportExport: string;
  reviewChecklistReportDecision: string;
  reviewChecklistSubmit: string;
  reviewChecklistSubmitting: string;
  reviewChecklistReset: string;

  // MM7 — public services / pricing / request + admin console
  servicesTitle: string;
  servicesIntro: string;
  servicesHumanReview: string;
  servicesNotLegal: string;
  servicesNoBilling: string;
  packPilotReady: string;
  packFoundation: string;
  packKnownGaps: string;
  packWorkflows: string;
  packRequestQuote: string;
  pricingTitle: string;
  pricingIntro: string;
  pricingDraftLabel: string;
  requestTitle: string;
  requestIntro: string;
  requestTypeLabel: string;
  requestTypeAccess: string;
  requestTypeDemo: string;
  requestTypeAudit: string;
  requestTypePilot: string;
  requestNameLabel: string;
  requestEmailLabel: string;
  requestCompanyLabel: string;
  requestCountryLabel: string;
  requestLanguageLabel: string;
  requestMessageLabel: string;
  requestSubmit: string;
  requestSubmitting: string;
  requestSuccess: string;
  requestNoEmailNote: string;
  requestValidationError: string;
  adminTitle: string;
  adminOverviewTab: string;
  adminReviewOpsTab: string;
  adminCommercialTab: string;
  adminSupportTab: string;
  adminBlockedMessage: string;
  adminCardUsers: string;
  adminCardRuns: string;
  adminCardReview: string;
  adminCardGaps: string;
  adminCardKnowledge: string;
  adminCardCost: string;
  adminCardRequests: string;
  adminAiModeLabel: string;
  adminBillingDisabled: string;

  // Final closure — persisted runs / artifacts / knowledge
  runSavedBanner: string;
  runSavedArtifactLink: string;
  navArtifacts: string;
  navKnowledge: string;
  navAdmin: string;
  artifactsTitle: string;
  artifactsIntro: string;
  artifactStatusLabel: string;
  artifactReviewApprove: string;
  artifactReviewReject: string;
  artifactExportDraft: string;
  artifactExportFinal: string;
  artifactExportFinalGated: string;
  artifactDownload: string;
  artifactSourceGaps: string;
  artifactEvidence: string;
  artifactVersions: string;
  knowledgeTitle: string;
  knowledgeIntro: string;
  knowledgeUploadLabel: string;
  knowledgeLanguageHint: string;
  knowledgeUploadButton: string;
  knowledgeExtractButton: string;
  knowledgeSearchLabel: string;
  knowledgeSearchButton: string;
  knowledgeWarnings: string;
  knowledgeNoOcrNote: string;
}

const en: UiStrings = {
  appName: "Packaging OS",
  switchLanguageLabel: "Switch language",
  copyrightLine: "© 2026 Packaging OS",

  trustBannerFull:
    "Draft surface. Not a final or commercial output. No regulatory guarantee. AI providers are not active in this phase.",
  trustBannerShort: "Draft surface. No regulatory guarantee.",
  trustNotesSourceBacked: "Source-backed: No",
  trustNotesRegulatoryGuarantee: "Regulatory guarantee: No",
  trustNotesFinalOutput: "Final output: No",

  ctaSignInWithGoogle: "Continue with Google",
  ctaRequestAccess: "Request access",
  ctaOpenDashboard: "Open workspace",
  ctaSignOut: "Sign out",
  ctaBackToLanding: "Back to home",

  landingHeroTitle:
    "Packaging Intelligence & Product Experience OS",
  landingHeroSubtitle:
    "A controlled professional workspace for packaging audit, label and copy review, material notes, claim risk, and supplier brief drafts. Operator-led. Trust-first.",
  landingTrustStripTitle: "Where the product stands today",
  landingTrustStripDraftItem:
    "Drafts only — no final or commercial output ships from this surface yet.",
  landingTrustStripNoLiveAiItem:
    "AI providers are not active in this phase. Engine output is deterministic and rule-based.",
  landingTrustStripControlledAccessItem:
    "Access is controlled by an admin. No self-serve registration.",
  landingCapabilityHeading: "What lives behind the gate",
  landingCapabilityCatalogTitle: "Catalog",
  landingCapabilityCatalogBody:
    "Brands, products, and packaging projects organized for review — with versioning and active-record discipline.",
  landingCapabilityWorkflowsTitle: "Workflows",
  landingCapabilityWorkflowsBody:
    "Dry-run packaging audit produces a structured draft scaffold for reviewers, with claim-risk flags and supplier-brief outline.",
  landingCapabilityTrustTitle: "Trust & Safety",
  landingCapabilityTrustBody:
    "Every output carries explicit draft / source / regulatory / review-required state. Reviewer pass is required before export.",
  landingWorkflowPreviewHeading: "How the audit flow works",
  landingWorkflowPreviewBody:
    "Operators submit a packaging brief — product, market, channel, claims. The deterministic engine returns a draft audit, label and copy notes, material notes, claim risk, supplier brief outline, and launch kit outline. Nothing is final until a reviewer signs off.",
  landingAccessHeading: "Access model",
  landingAccessBody:
    "Roles are assigned by an admin in the platform identity gateway. Preview accounts can browse the catalog only; editors and brand owners can run dry-run audits; reviewers approve. No self-serve elevation.",
  landingCtaSectionHeading: "Ready to enter?",
  landingCtaSectionBody:
    "Sign in if you already have a role assigned. Otherwise, request access — an admin reviews each request manually.",
  landingFooterEcosystem:
    "Packaging OS is a vertical app on the ai-platform-core ecosystem. Identity, audit, and AI execution are delegated to Core.",

  loginTitle: "Sign in to Packaging OS",
  loginGoogleHelperText:
    "Sign-in is handled by the Core identity gateway. Packaging OS never sees your password.",
  loginEmailComingSoonLabel: "Email and password — coming soon",
  loginEmailComingSoonTooltip:
    "Email and password sign-in is not enabled in this phase. We require an admin-provisioned account in ai-platform-core.",
  loginRequestAccessLead: "Don't have access yet?",
  loginTrustParagraph:
    "Your role is assigned by an admin. Sign-in confirms identity but does not grant additional access.",
  loginCoreUrlMissingNotice:
    "Core sign-in URL is not configured in this build. The operator must set VITE_CORE_AUTH_START_URL before sign-in is reachable.",
  loginEmailLabel: "Email",
  loginPasswordLabel: "Password",
  loginSubmitLabel: "Sign in",
  loginSubmittingLabel: "Signing in…",
  loginErrorInvalidCredentials: "Invalid email or password.",
  loginErrorSuspended:
    "This account is suspended. Please contact the platform admin.",
  loginErrorUnavailable:
    "Sign-in is temporarily unavailable. Please try again.",
  loginGoogleUnavailableNotice:
    "Google sign-in is not available yet. Use email and password.",
  loginPreviewModeNotice:
    "Your account is in Preview Mode. To activate full access, please contact the platform admin.",
  loginPreviewContinue: "Continue in Preview Mode",
  loginErrorOauthCancelled:
    "Sign-in was cancelled. You can try again or request access.",
  loginErrorOauthFailed:
    "Sign-in could not complete. Please try again. If the problem persists, contact your admin.",
  loginErrorServiceDown:
    "We can't reach the sign-in service right now. Please retry shortly.",
  loginPostauthChecking: "Completing sign-in…",
  loginPostauthSessionMissing:
    "Sign-in returned, but the Packaging session is not available yet. Please try again or contact the operator.",
  loginPostauthAccessDenied:
    "Sign-in succeeded, but your account does not have access to this workspace. Contact the operator.",
  loginPostauthUnavailable:
    "We could not verify the session. Please try again shortly.",
  loginPostauthContinue: "Continue to workspace",

  previewTitle: "You're in preview mode.",
  previewBanner:
    "The catalog is read-only and the audit workflow is gated behind an admin-assigned role.",
  previewYouCanHeading: "What you can do",
  previewYouCanItem1: "See your access summary and roles.",
  previewYouCanItem2: "Read the public landing and trust posture.",
  previewLockedHeading: "What's locked",
  previewLockedAuditReason:
    "Run a packaging audit — requires the packaging.workflow.start permission.",
  previewLockedExportReason:
    "Export results — Phase 2, pending real workflow execution.",
  previewLockedReviewReason:
    "Send to reviewer — requires a reviewer role.",
  previewLockedSupplierReason:
    "Generate full supplier brief — Phase 2, pending real workflow execution.",
  previewContactAdminLead: "Trouble?",
  previewRequestAccessNotice:
    "Access is granted manually by an admin after review. There is no automatic upgrade or payment in this phase.",

  appShellTitle: "Authenticated workspace shell",
  appShellSubtitle:
    "Workspace shell is staged. Detailed catalog and workflow screens open in the next phase.",
  appShellWorkspacePending:
    "Catalog and dry-run workflow surfaces are not yet rendered here. The protected backend already serves them.",
  appShellApiStatusHeading: "API status",
  appShellNavCatalog: "Catalog",
  appShellNavWorkflows: "Workflows",
  appShellNavTrust: "Trust & Safety",
  appShellNavSettings: "Settings",
  appShellSectionPlaceholder:
    "This section opens in the next phase.",
  appShellComingSoonBrands: "Brands list — opening in Phase 1O-B.",
  appShellComingSoonProducts: "Products list — opening in Phase 1O-B.",
  appShellComingSoonProjects: "Packaging projects list — opening in Phase 1O-B.",
  appShellComingSoonAudit: "Dry-run packaging audit — opening in Phase 1O-B.",

  whoAmIChecking: "Checking your session…",
  whoAmIOkLead: "Signed in as",
  whoAmIRoles: "Roles",
  whoAmIPermissionsCount: "Permissions",
  whoAmIAccessModeLabel: "Access mode",
  whoAmIPreviewLabel: "Preview mode",
  accessModeSuperAdmin: "Super admin",
  accessModeAdmin: "Admin",
  accessModeOperator: "Operator",
  accessModeUser: "User",
  accessModePreview: "Preview access",
  accessModeUnknown: "Unknown",

  errAuthMissing: "Sign in to access the operator workspace.",
  errAuthInvalid: "Your session expired. Please sign in again.",
  errPermissionDenied:
    "Your role does not include the required permission for this action.",
  errRateLimited:
    "Too many requests. Please wait a moment and try again.",
  errServiceUnavailable:
    "The Packaging OS service is temporarily degraded. Please retry shortly.",
  errNetwork:
    "We could not reach the Packaging OS API. Check your connection and try again.",
  errUnknown:
    "An unexpected error occurred. Please retry, and share the request id with support if it persists.",
  errRequestIdLabel: "Request:",

  notFoundTitle: "Page not found",
  notFoundBody:
    "We could not find the page you were looking for. Use the navigation to return to a known section.",

  catalogBrandsTitle: "Brands",
  catalogProductsTitle: "Products",
  catalogProjectsTitle: "Packaging projects",
  catalogColumnName: "Name",
  catalogColumnSlug: "Slug",
  catalogColumnVersion: "Version",
  catalogColumnUpdated: "Updated",
  catalogColumnCategory: "Category",
  catalogColumnStatus: "Status",
  catalogColumnMarkets: "Markets",
  catalogColumnLanguages: "Languages",
  catalogEmptyTitle: "No records yet",
  catalogEmptyBody:
    "No data is registered for this section yet. Records will appear here after an admin completes the initial setup.",
  catalogPreviewRestrictedTitle: "Preview access only",
  catalogPreviewRestrictedBody:
    "Your account is in preview mode and does not have permission to view or run this section. Contact the project operator to enable access.",
  catalogPreviewRestrictedCta: "Request access",
  catalogPaginationLabel: "Page",
  catalogPrevPage: "Previous",
  catalogNextPage: "Next",
  catalogReadOnlyNotice:
    "In this phase the catalog is available for viewing and initial review only. Adding or editing records will be enabled later.",

  workflowDryRunTitle: "Run a packaging dry-run",
  workflowDryRunSubtitle:
    "The output is a structured DRAFT for reviewer eyes only. The deterministic engine does not call any AI provider in this phase.",
  workflowDryRunFieldProductName: "Product name",
  workflowDryRunFieldProductCategory: "Product category",
  workflowDryRunFieldMarket: "Target market",
  workflowDryRunFieldChannel: "Sales channel",
  workflowDryRunFieldPackageType: "Package type",
  workflowDryRunFieldMaterial: "Material preference",
  workflowDryRunFieldClaims: "Claims",
  workflowDryRunFieldClaimsHelp:
    "One claim per line. Up to 32 claims, 120 characters each.",
  workflowDryRunFieldTargetCustomer: "Target customer",
  workflowDryRunFieldLanguage: "Output language",
  workflowDryRunFieldNotes: "Notes",
  workflowDryRunSubmit: "Run dry-run audit",
  workflowDryRunSubmitting: "Generating draft…",
  workflowDryRunReset: "Reset",
  workflowDryRunRequiredHint: "Required",
  workflowDryRunNotSelected: "— not selected —",
  workflowDryRunValidationFailed:
    "Please review the highlighted fields and try again.",

  aiAssistToggleLabel: "AI-assisted overlay",
  aiAssistToggleHint:
    "Adds an advisory AI overlay on top of the deterministic draft. The deterministic engine still runs first and is authoritative.",
  aiAssistDraftBanner:
    "AI-assisted draft, still requires human review. No regulatory approval, no commercial guarantee.",
  aiAssistStatusOk: "AI-assisted overlay attached",
  aiAssistStatusFallback:
    "AI gateway unavailable — deterministic draft shown. Reviewer pass still required.",
  aiAssistStatusOff: "Deterministic draft only — AI assist disabled.",
  aiAssistAuditHeader: "AI audit",

  reportInputSummary: "Input summary",
  reportPackagingAudit: "Packaging audit",
  reportPackagingAuditPositioning: "Positioning notes",
  reportPackagingAuditPackStructure: "Pack structure notes",
  reportPackagingAuditShelf: "Shelf and delivery considerations",
  reportLabelCopy: "Label / copy suggestions",
  reportLabelCopyFront: "Front of pack",
  reportLabelCopySide: "Side panel",
  reportLabelCopyTone: "Tone notes",
  reportMaterialNotes: "Material notes",
  reportClaimRisk: "Claim risk",
  reportClaimRiskLevelLabel: "Risk level",
  reportClaimRiskFlags: "Flags",
  reportClaimRiskReviewRequired: "Review required",
  reportClaimRiskReviewReasons: "Reasons",
  reportSupplierBrief: "Supplier brief outline",
  reportLaunchKit: "Launch kit outline",
  reportTrustNotes: "Trust notes",
  reportNextSteps: "Next steps",
  reportRiskLow: "low",
  reportRiskMedium: "medium",
  reportRiskHigh: "high",
  reportReviewRequiredYes: "Yes",
  reportReviewRequiredNo: "No",
  reportHaltBannerHigh:
    "High-risk claims detected. Halt all packaging proofing until a claim-risk reviewer AND a regulatory advisor sign off. Do not export.",
  reportHaltBannerMedium:
    "Sensitive claim wording detected. A qualified reviewer must approve copy before export.",
  reportNoFlags: "No flags. Reviewer pass not required at this risk level.",

  intakeTitle: "Product Brief + Brand Intake",
  intakeSubtitle:
    "Capture a structured product, brand, market, objective, constraint, and asset brief. The deterministic engine returns a DRAFT scaffold for reviewer use only — no AI provider runs in this phase.",
  intakeGroupProduct: "Product basics",
  intakeGroupBrand: "Brand context",
  intakeGroupMarket: "Market & channel",
  intakeGroupObjective: "Packaging objective",
  intakeGroupConstraints: "Constraints",
  intakeGroupAssets: "Assets available",
  intakeFieldProductName: "Product name",
  intakeFieldProductCategory: "Product category",
  intakeFieldProductSubcategory: "Subcategory",
  intakeFieldProductDescription: "Product description",
  intakeFieldProductForm: "Product form",
  intakeFieldSizeValue: "Size value",
  intakeFieldSizeUnit: "Size unit",
  intakeFieldPriceBand: "Price band",
  intakeFieldBrandName: "Brand name",
  intakeFieldBrandPersonality: "Brand personality",
  intakeFieldBrandMaturity: "Brand maturity",
  intakeFieldCurrentPackagingStatus: "Current packaging",
  intakeFieldVisualPreferences: "Visual preferences",
  intakeFieldToneOfVoice: "Tone of voice",
  intakeFieldTargetMarkets: "Target markets",
  intakeFieldTargetMarketsHelp:
    "Comma-separated ISO-2 country codes (e.g. AE, SA, TR). Up to 8.",
  intakeFieldTargetLanguages: "Target languages",
  intakeFieldSalesChannels: "Sales channels",
  intakeFieldTargetAudience: "Target audience",
  intakeFieldObjectives: "Objectives",
  intakeFieldObjectivesHelp:
    "Pick at least one. Multiple are allowed.",
  intakeFieldMaterialConstraints: "Material constraints",
  intakeFieldBudgetBand: "Budget band",
  intakeFieldProductionConstraints: "Production constraints",
  intakeFieldSustainabilityPreferences: "Sustainability preferences",
  intakeFieldSustainabilityPreferencesHelp:
    "Short tags, one per line. Up to 8.",
  intakeFieldClaimsToAvoid: "Claims to avoid",
  intakeFieldClaimsToAvoidHelp:
    "One claim per line. Up to 32, 120 characters each.",
  intakeFieldAssetsAvailable: "Assets available",
  intakeFieldLanguage: "Output language",
  intakeFieldNotes: "Notes",
  intakeSubmit: "Run product brief intake",
  intakeSubmitting: "Generating draft…",
  intakeReset: "Reset",
  intakeReportProductBrief: "Product brief summary",
  intakeReportBrandContext: "Brand context summary",
  intakeReportObjectiveSummary: "Packaging objective summary",
  intakeReportMarketChannelFit: "Market and channel fit notes",
  intakeReportMissingChecklist: "Missing information checklist",
  intakeReportAssumptionLog: "Assumption log",
  intakeReportInitialRiskFlags: "Initial risk flags",
  intakeReportHumanReview: "Human review required notes",
  intakeReportRecommendedNextEngines: "Recommended next engines",

  intakeReportStrategy: "Packaging strategy snapshot",
  intakeReportTargetCustomer: "Target customer interpretation",
  intakeReportPositioning: "Positioning notes",
  intakeReportChannelReadiness: "Channel readiness notes",
  intakeReportLocalization: "Language and localization notes",
  intakeReportDecisionPriorities: "Packaging decision priorities",
  intakeReportOwnerQuestions: "Owner questions to resolve",
  intakeNextLabelCopy: "Next: Label & claim review",

  labelCopyTitle: "Label & claim review",
  labelCopySubtitle:
    "Deterministic claim-risk scan + label-copy scaffold. Risky claims are NEVER rewritten as approved. The engine emits no regulatory citation.",
  labelCopyFieldProductName: "Product name",
  labelCopyFieldCategory: "Product category",
  labelCopyFieldBrandName: "Brand name",
  labelCopyFieldTargetMarket: "Target market (ISO-2)",
  labelCopyFieldTargetLanguage: "Target language",
  labelCopyFieldSalesChannel: "Sales channel",
  labelCopyFieldProductDescription: "Product description",
  labelCopyFieldPackagingObjective: "Packaging objective",
  labelCopyFieldClaimDrafts: "Claim / copy drafts",
  labelCopyFieldClaimDraftsHelp:
    "One claim per line. Up to 32, 200 characters each.",
  labelCopyFieldToneOfVoice: "Tone of voice",
  labelCopyFieldClaimsToAvoid: "Claims to avoid",
  labelCopyFieldRequiredLabelElements: "Required label elements",
  labelCopyFieldRequiredLabelElementsHelp:
    "One element per line. Up to 12.",
  labelCopySubmit: "Run label & claim review",
  labelCopySubmitting: "Generating draft…",
  labelCopyReset: "Reset",
  labelCopyReportFront: "Front label draft",
  labelCopyReportBack: "Back label draft",
  labelCopyReportStory: "Short product story",
  labelCopyReportInstructions: "Usage / serving / handling",
  labelCopyReportWarning: "Warning / caution",
  labelCopyReportClaimRiskTable: "Claim risk table",
  labelCopyReportSaferWording: "Safer wording suggestions",
  labelCopyReportMissingInfo: "Missing label information",
  labelCopyReportHumanReview: "Human review required",
  labelCopyReportCopyQuality: "Copy quality notes",
  labelCopyClaimColClaim: "Claim",
  labelCopyClaimColSeverity: "Severity",
  labelCopyClaimColCategory: "Category",
  labelCopyClaimColMatched: "Matched terms",
  labelCopyClaimColReview: "Review",

  materialTitle: "Material & structure",
  materialSubtitle:
    "Deterministic packaging-format / material / structure recommendation. No engineering or manufacturing guarantee. Human review required.",
  materialFieldFragility: "Fragility",
  materialFieldTemperatureSensitive: "Temperature sensitive",
  materialFieldSustainabilityPreferences: "Sustainability preferences",
  materialReportFormat: "Recommended packaging format",
  materialReportMaterial: "Material recommendation",
  materialReportStructure: "Structure recommendation",
  materialReportDurability: "Durability / handling",
  materialReportDelivery: "Delivery suitability",
  materialReportEcommerce: "E-commerce suitability",
  materialReportShelf: "Shelf suitability",
  materialReportProduction: "Production feasibility",
  materialReportCost: "Cost sensitivity",
  materialReportSupplierQuestions: "Open questions for supplier",
  materialSubmit: "Run material & structure",
  materialSubmitting: "Generating draft…",
  materialReset: "Reset",
  intakeNextMaterial: "Next: Material & structure",

  sustTitle: "Sustainability & compliance risk",
  sustSubtitle:
    "Deterministic greenwashing / sustainability / compliance scan. The engine emits no regulatory citation and never approves a claim.",
  sustFieldPackagingMaterial: "Packaging material",
  sustFieldClaims: "Sustainability claims",
  sustFieldClaimsHelp: "One claim per line. Up to 32, 200 chars each.",
  sustFieldLabelDrafts: "Label / copy drafts (optional)",
  sustFieldRecyclable: "Has recyclable claim",
  sustFieldBiodegradable: "Has biodegradable claim",
  sustFieldCompostable: "Has compostable claim",
  sustFieldOrigin: "Has origin / made-in claim",
  sustFieldBarcode: "Barcode present on label",
  sustFieldExpiry: "Expiry / best-before present",
  sustFieldOriginLabel: "Country-of-origin label present",
  sustFieldWarning: "Required warnings present",
  sustReportClaimReview: "Sustainability claim review",
  sustReportGreenwashing: "Greenwashing risk flags",
  sustReportSeverityTable: "Claim severity table",
  sustReportSafer: "Safer sustainability wording",
  sustReportCompliance: "Compliance reminder checklist",
  sustReportCountry: "Country / region notes",
  sustReportEvidence: "Required evidence",
  sustReportNotApproved: "DRAFT — not approved",
  sustSubmit: "Run sustainability & compliance risk",
  sustSubmitting: "Generating draft…",
  sustReset: "Reset",
  sustColTerm: "Term",
  sustColSeverity: "Severity",
  sustColCategory: "Category",
  sustColEvidence: "Evidence required",
  labelCopyNextSust: "Next: Sustainability & compliance risk",

  supplierTitle: "Supplier brief / RFQ",
  supplierSubtitle:
    "Deterministic supplier brief and RFQ draft. The engine NEVER sends, NEVER selects a supplier, and NEVER claims a final price or manufacturing approval.",
  supplierFieldFormat: "Packaging format",
  supplierFieldMaterial: "Packaging material",
  supplierFieldStructure: "Structure recommendation",
  supplierFieldQuantity: "Quantity range",
  supplierFieldPrintFinish: "Print / finish",
  supplierFieldKnownConstraints: "Known constraints",
  supplierFieldQuestions: "Questions for supplier",
  supplierFieldQuestionsHelp: "One per line. Up to 12.",
  supplierReportBrief: "Supplier brief summary",
  supplierReportSpec: "Packaging specification draft",
  supplierReportMaterial: "Material / structure requirements",
  supplierReportPrint: "Print / finish requirements",
  supplierReportQuantity: "Quantity / MOQ questions",
  supplierReportLead: "Lead time questions",
  supplierReportQuality: "Quality / testing questions",
  supplierReportSust: "Sustainability / evidence questions",
  supplierReportRfqEmail: "RFQ email draft (engine does NOT send)",
  supplierReportCompare: "Supplier comparison criteria",
  supplierReportMissing: "Missing production information",
  supplierSubmit: "Run supplier brief / RFQ",
  supplierSubmitting: "Generating draft…",
  supplierReset: "Reset",
  materialNextSupplier: "Next: Supplier brief / RFQ",
  sustNextSupplier: "Next: Supplier brief / RFQ (request evidence)",

  visualTitle: "Visual direction",
  visualSubtitle:
    "Deterministic visual direction brief. The engine NEVER renders an image, NEVER copies competitor artwork, and NEVER approves final artwork.",
  visualFieldBrandPersonality: "Brand personality",
  visualFieldTargetCustomer: "Target customer",
  visualFieldVisualPreference: "Visual preference",
  visualFieldColorsToUse: "Colors to use",
  visualFieldColorsToAvoid: "Colors to avoid",
  visualFieldTypographyPreference: "Typography preference",
  visualFieldCompetitorReferences: "Competitor references",
  visualFieldCompetitorReferencesHelp:
    "One per line. For inspiration only — never copied.",
  visualReportSummary: "Visual direction summary",
  visualReportRoutes: "Design route options",
  visualReportColor: "Color direction",
  visualReportTypography: "Typography direction",
  visualReportLayout: "Layout direction",
  visualReportSurface: "Packaging surface priority",
  visualReportShelfEc: "Shelf / e-commerce visual notes",
  visualReportCultural: "Cultural / localization notes",
  visualReportMockup: "Mockup prompt pack",
  visualReportHandoff: "Designer handoff notes",
  visualReportRisks: "Visual risks / avoidance",
  visualSubmit: "Run visual direction",
  visualSubmitting: "Generating draft…",
  visualReset: "Reset",
  intakeNextVisual: "Next: Visual direction",
  labelCopyNextVisual: "Next: Visual direction",

  scorecardTitle: "Packaging scorecard & vertical pack",
  scorecardSubtitle:
    "Deterministic 0–100 sub-scores for shelf / ecommerce / delivery / unboxing / marketplace, biased by the chosen vertical pack. No image analysis. Human review required.",
  scorecardFieldVerticalPack: "Vertical pack",
  scorecardFieldShelfEnv: "Shelf environment",
  scorecardFieldFragility: "Fragility",
  scorecardFieldLiquid: "Is liquid",
  scorecardFieldTempSensitive: "Temperature sensitive",
  scorecardFieldThumbnailOpt: "Ecommerce thumbnail optimized",
  scorecardFieldProtectiveInsert: "Has protective insert",
  scorecardFieldLeakSeal: "Has leak-proof seal",
  scorecardFieldDropTest: "Drop test passed",
  scorecardFieldVisualNotes: "Visual direction notes",
  scorecardOverall: "Overall packaging score",
  scorecardSectionShelf: "Shelf visibility",
  scorecardSectionEcommerce: "E-commerce thumbnail readiness",
  scorecardSectionDelivery: "Delivery durability",
  scorecardSectionUnboxing: "Unboxing experience",
  scorecardSectionMarketplace: "Marketplace readiness",
  scorecardVerticalPackLabel: "Vertical pack",
  scorecardRiskFlags: "Risk flags",
  scorecardImprovements: "Improvement suggestions",
  scorecardChannelRecs: "Channel-specific recommendations",
  scorecardMissing: "Missing information",
  scorecardSubmit: "Run packaging scorecard",
  scorecardSubmitting: "Generating draft…",
  scorecardReset: "Reset",
  scorecardBandHigh: "high",
  scorecardBandMedium: "medium",
  scorecardBandLow: "low",
  visualNextScorecard: "Next: Packaging scorecard",
  supplierNextScorecard: "Next: Packaging scorecard",
  launchKitTitle: "Launch kit",
  launchKitSubtitle: "Deterministic launch-kit copy DRAFTS for human review. The engine NEVER posts and NEVER claims final approval.",
  launchKitFieldPositioning: "Packaging positioning",
  launchKitFieldKeyBenefit: "Key benefit",
  launchKitFieldClaimRestrictions: "Claim restrictions",
  launchKitFieldQrConnected: "QR / connected packaging",
  launchKitFieldLaunchChannels: "Launch channels",
  launchKitReportPositioning: "Launch positioning summary",
  launchKitReportListing: "E-commerce listing draft",
  launchKitReportSocial: "Social content drafts",
  launchKitReportInsert: "Insert card copy",
  launchKitReportQr: "QR / connected idea",
  launchKitReportLoyalty: "Review / reorder / loyalty copy",
  launchKitReportSafety: "Claim safety notes",
  launchKitSubmit: "Run launch kit",
  launchKitSubmitting: "Generating draft…",
  launchKitReset: "Reset",
  postLaunchTitle: "Post-launch analysis",
  postLaunchSubtitle: "Operator-pasted only. No external scraping, no marketplace API, no PII processing, no statistical claim.",
  postLaunchFieldReviews: "Review snippets (one per line)",
  postLaunchFieldReturns: "Return reasons (one per line)",
  postLaunchFieldFeedback: "Customer feedback notes (one per line)",
  postLaunchFieldPerformance: "E-commerce performance notes",
  postLaunchFieldVersion: "Packaging version",
  postLaunchReportThemes: "Feedback theme summary",
  postLaunchReportComplaints: "Packaging complaint classification",
  postLaunchReportReturns: "Return / damage classification",
  postLaunchReportSentiment: "Review sentiment notes",
  postLaunchReportImprovements: "Packaging improvement suggestions",
  postLaunchReportNext: "Next iteration priorities",
  postLaunchReportDataQuality: "Data quality / missing data",
  postLaunchSubmit: "Run post-launch analysis",
  postLaunchSubmitting: "Generating draft…",
  postLaunchReset: "Reset",
  reviewChecklistTitle: "Review checklist",
  reviewChecklistSubtitle: "Deterministic checklist + risk review template + export readiness. No DB, no persistence, no final approval.",
  reviewChecklistFieldTarget: "Workflow target",
  reviewChecklistFieldHighRisk: "High-risk claim present",
  reviewChecklistFieldEvidenceGap: "Evidence gap present",
  reviewChecklistFieldSustClaim: "Unsupported sustainability claim",
  reviewChecklistFieldFoodContact: "Food-contact concern",
  reviewChecklistFieldCosmetic: "Cosmetic safety concern",
  reviewChecklistFieldIndustrial: "Industrial hazard concern",
  reviewChecklistFieldSupplier: "Supplier uncertainty",
  reviewChecklistReportChecklist: "Review checklist",
  reviewChecklistReportCriteria: "Approval criteria",
  reviewChecklistReportRisks: "Risk review items",
  reviewChecklistReportEvidence: "Evidence required",
  reviewChecklistReportExport: "Export readiness",
  reviewChecklistReportDecision: "Reviewer decision template",
  reviewChecklistSubmit: "Run review checklist",
  reviewChecklistSubmitting: "Generating draft…",
  reviewChecklistReset: "Reset",

  servicesTitle: "Services & packaging intelligence packs",
  servicesIntro:
    "Packaging OS helps teams make better packaging decisions before spending on design, printing, suppliers, claims or launch. Every deliverable is a draft decision-support pack.",
  servicesHumanReview: "Human review required on every output",
  servicesNotLegal: "Not legal or regulatory approval",
  servicesNoBilling: "No online billing — engagement starts with a request",
  packPilotReady: "Pilot requests open",
  packFoundation: "Commercial foundation",
  packKnownGaps: "Known gaps",
  packWorkflows: "Included workflows",
  packRequestQuote: "Request a quote",
  pricingTitle: "Packages & pricing",
  pricingIntro:
    "Pricing is finalized per engagement. No checkout or subscription exists — submit a request and a human will contact you.",
  pricingDraftLabel: "Pricing draft — request a quote",
  requestTitle: "Request access, a demo, an audit or a pilot",
  requestIntro:
    "Tell us about your product and market. Requests are stored and reviewed by a human — no automated emails are sent.",
  requestTypeLabel: "What do you need?",
  requestTypeAccess: "Platform access",
  requestTypeDemo: "Demo",
  requestTypeAudit: "Packaging audit",
  requestTypePilot: "Pilot project",
  requestNameLabel: "Your name",
  requestEmailLabel: "Email",
  requestCompanyLabel: "Company / brand",
  requestCountryLabel: "Country / market",
  requestLanguageLabel: "Preferred language",
  requestMessageLabel: "Message (optional)",
  requestSubmit: "Submit request",
  requestSubmitting: "Submitting…",
  requestSuccess: "Your request was stored. The team will review it and contact you.",
  requestNoEmailNote: "No automated email is sent at this stage.",
  requestValidationError: "Please provide a valid name and email address.",
  adminTitle: "Admin console",
  adminOverviewTab: "Overview",
  adminReviewOpsTab: "Review operations",
  adminCommercialTab: "Commercial",
  adminSupportTab: "Support",
  adminBlockedMessage: "This console requires owner/admin permissions.",
  adminCardUsers: "Users",
  adminCardRuns: "Workflow runs",
  adminCardReview: "Artifacts needing review",
  adminCardGaps: "Open source gaps",
  adminCardKnowledge: "Knowledge files",
  adminCardCost: "Total cost (USD)",
  adminCardRequests: "New commercial requests",
  adminAiModeLabel: "AI mode",
  adminBillingDisabled: "Billing disabled — no payment provider",
  runSavedBanner: "Run saved — draft artifact created (human review required)",
  runSavedArtifactLink: "Open artifact",
  navArtifacts: "Artifacts & exports",
  navKnowledge: "Knowledge",
  navAdmin: "Admin console",
  artifactsTitle: "Artifacts & exports",
  artifactsIntro: "Every saved run creates a draft artifact. Review, approve and export here — final exports stay gated until approval.",
  artifactStatusLabel: "Status",
  artifactReviewApprove: "Approve",
  artifactReviewReject: "Reject",
  artifactExportDraft: "Draft export (MD)",
  artifactExportFinal: "Final export (PDF)",
  artifactExportFinalGated: "Final export requires an approved artifact.",
  artifactDownload: "Download",
  artifactSourceGaps: "Open source gaps",
  artifactEvidence: "Linked evidence",
  artifactVersions: "Versions",
  knowledgeTitle: "Knowledge brain",
  knowledgeIntro: "Upload reference documents (PDF/DOCX/TXT/CSV/XLSX). Uploaded content is not truth — extraction warnings stay visible and evidence needs human verification.",
  knowledgeUploadLabel: "Document file",
  knowledgeLanguageHint: "Source language (declared)",
  knowledgeUploadButton: "Upload",
  knowledgeExtractButton: "Extract",
  knowledgeSearchLabel: "Search workspace knowledge",
  knowledgeSearchButton: "Search",
  knowledgeWarnings: "Extraction warnings",
  knowledgeNoOcrNote: "Scanned/image PDFs are not supported (no OCR) — they fail honestly with a warning.",
};

const ar: UiStrings = {
  appName: "Packaging OS",
  switchLanguageLabel: "تبديل اللغة",
  copyrightLine: "© 2026 Packaging OS",

  trustBannerFull:
    "هذه واجهة مسودة. ليست مخرجات نهائية أو تجارية. لا يوجد ضمان تنظيمي. مزودات الذكاء الاصطناعي غير مفعّلة في هذه المرحلة.",
  trustBannerShort: "واجهة مسودة. لا يوجد ضمان تنظيمي.",
  trustNotesSourceBacked: "مدعوم بمصدر: لا",
  trustNotesRegulatoryGuarantee: "ضمان تنظيمي: لا",
  trustNotesFinalOutput: "مخرج نهائي: لا",

  ctaSignInWithGoogle: "المتابعة بحساب Google",
  ctaRequestAccess: "طلب الوصول",
  ctaOpenDashboard: "فتح بيئة العمل",
  ctaSignOut: "تسجيل الخروج",
  ctaBackToLanding: "العودة إلى الصفحة الرئيسية",

  landingHeroTitle:
    "نظام ذكاء التغليف وتجربة المنتج",
  landingHeroSubtitle:
    "بيئة عمل احترافية مُتحكَّم بها لتدقيق التغليف، ومراجعة الملصقات والنصوص، وملاحظات المواد، ومخاطر الادعاءات، ومسودات بريف المورّدين. يقودها المشغّلون. الثقة أولاً.",
  landingTrustStripTitle: "أين يقف المنتج اليوم",
  landingTrustStripDraftItem:
    "مسودات فقط — لا مخرجات نهائية أو تجارية تُسلَّم من هذه الواجهة بعد.",
  landingTrustStripNoLiveAiItem:
    "مزودات الذكاء الاصطناعي غير مفعّلة في هذه المرحلة. مخرجات المحرّك حتمية وقائمة على القواعد.",
  landingTrustStripControlledAccessItem:
    "الوصول يُمنح من قِبل المشرف. لا يوجد تسجيل ذاتي.",
  landingCapabilityHeading: "ما يقع خلف البوابة",
  landingCapabilityCatalogTitle: "الكتالوج",
  landingCapabilityCatalogBody:
    "العلامات التجارية والمنتجات ومشاريع التغليف منظَّمة للمراجعة، مع انضباط الإصدارات والسجلات النشطة.",
  landingCapabilityWorkflowsTitle: "سير العمل",
  landingCapabilityWorkflowsBody:
    "تدقيق التغليف بصيغة تشغيل تجريبي ينتج مسودة هيكلية للمراجعين، مع علامات مخاطر للادعاءات ومخطط بريف المورّد.",
  landingCapabilityTrustTitle: "الثقة والأمان",
  landingCapabilityTrustBody:
    "كل مخرَج يحمل حالة صريحة: مسودة / مصدر / تنظيمي / مطلوب-مراجعة. مراجعة بشرية لازمة قبل التصدير.",
  landingWorkflowPreviewHeading: "كيف تعمل عملية التدقيق",
  landingWorkflowPreviewBody:
    "يقدّم المشغّلون بريف تغليف — منتج، سوق، قناة، ادعاءات. يُرجع المحرّك الحتمي مسودة تدقيق، وملاحظات الملصق والنصوص، وملاحظات المواد، ومخاطر الادعاءات، ومخطط بريف المورّد، ومخطط حقيبة الإطلاق. لا شيء نهائي قبل اعتماد المراجع.",
  landingAccessHeading: "نموذج الوصول",
  landingAccessBody:
    "تُسنَد الأدوار من قِبل المشرف في بوابة الهوية. حسابات المعاينة تتصفح الكتالوج فقط؛ المحررون ومالكو العلامة يشغّلون التدقيق التجريبي؛ المراجعون يعتمدون. لا ترقية ذاتية.",
  landingCtaSectionHeading: "هل أنت مستعد للدخول؟",
  landingCtaSectionBody:
    "سجّل الدخول إن كان لديك دور مسبَق. وإلا فاطلب الوصول — يراجع المشرف كل طلب يدوياً.",
  landingFooterEcosystem:
    "Packaging OS تطبيق رأسي ضمن منظومة ai-platform-core. الهوية والتدقيق وتنفيذ الذكاء الاصطناعي مفوَّضة إلى Core.",

  loginTitle: "تسجيل الدخول إلى Packaging OS",
  loginGoogleHelperText:
    "يُدار تسجيل الدخول عبر بوابة الهوية في Core. لا تطّلع Packaging OS على كلمة المرور.",
  loginEmailComingSoonLabel: "البريد وكلمة المرور — قريباً",
  loginEmailComingSoonTooltip:
    "تسجيل الدخول بالبريد وكلمة المرور غير مفعَّل في هذه المرحلة. يتطلّب حساباً مُعَدّاً مسبقاً في ai-platform-core.",
  loginRequestAccessLead: "ليس لديك وصول بعد؟",
  loginTrustParagraph:
    "يُسنَد دورك من قِبل المشرف. تسجيل الدخول يؤكّد الهوية ولا يمنح صلاحيات إضافية.",
  loginCoreUrlMissingNotice:
    "عنوان تسجيل الدخول في Core غير مُعَدّ في هذا البناء. يجب على المشغّل تعيين VITE_CORE_AUTH_START_URL قبل أن يصبح تسجيل الدخول متاحاً.",
  loginEmailLabel: "البريد الإلكتروني",
  loginPasswordLabel: "كلمة المرور",
  loginSubmitLabel: "تسجيل الدخول",
  loginSubmittingLabel: "جارٍ تسجيل الدخول…",
  loginErrorInvalidCredentials: "البريد الإلكتروني أو كلمة المرور غير صحيحة.",
  loginErrorSuspended:
    "هذا الحساب موقوف. يرجى التواصل مع مشرف المنصة.",
  loginErrorUnavailable:
    "تسجيل الدخول غير متاح مؤقتاً. يرجى المحاولة مرة أخرى.",
  loginGoogleUnavailableNotice:
    "تسجيل الدخول عبر Google غير متاح بعد. استخدم البريد وكلمة المرور.",
  loginPreviewModeNotice:
    "حسابك في وضع المعاينة. لتفعيل الوصول الكامل، يرجى التواصل مع مشرف المنصة.",
  loginPreviewContinue: "المتابعة في وضع المعاينة",
  loginErrorOauthCancelled:
    "تم إلغاء تسجيل الدخول. يمكنك المحاولة مرة أخرى أو طلب الوصول.",
  loginErrorOauthFailed:
    "تعذّر إكمال تسجيل الدخول. حاول مجدداً، وإن استمر الخطأ تواصل مع المشرف.",
  loginErrorServiceDown:
    "تعذّر الوصول إلى خدمة تسجيل الدخول حالياً. أعِد المحاولة بعد قليل.",
  loginPostauthChecking: "جارٍ إكمال تسجيل الدخول…",
  loginPostauthSessionMissing:
    "عاد تسجيل الدخول، لكن جلسة Packaging غير متاحة بعد. حاول مجدداً أو تواصل مع المشرف.",
  loginPostauthAccessDenied:
    "تم تسجيل الدخول، لكن حسابك لا يملك صلاحية الوصول إلى بيئة العمل هذه. تواصل مع المشرف.",
  loginPostauthUnavailable:
    "تعذّر التحقق من الجلسة. حاول مجدداً بعد قليل.",
  loginPostauthContinue: "الانتقال إلى بيئة العمل",

  previewTitle: "أنت في وضع المعاينة.",
  previewBanner:
    "الكتالوج للقراءة فقط، وسير عمل التدقيق محجوب خلف دور يُسنَده المشرف.",
  previewYouCanHeading: "ما يمكنك فعله",
  previewYouCanItem1: "الاطلاع على ملخّص وصولك وأدوارك.",
  previewYouCanItem2: "قراءة الصفحة العامة وموقف الثقة.",
  previewLockedHeading: "ما هو محجوب",
  previewLockedAuditReason:
    "تشغيل تدقيق التغليف — يتطلّب صلاحية packaging.workflow.start.",
  previewLockedExportReason:
    "تصدير النتائج — المرحلة 2، بانتظار تنفيذ سير العمل الحقيقي.",
  previewLockedReviewReason:
    "إرسال إلى المراجع — يتطلّب دور مراجع.",
  previewLockedSupplierReason:
    "إنشاء بريف المورّد الكامل — المرحلة 2، بانتظار تنفيذ سير العمل الحقيقي.",
  previewContactAdminLead: "هل تواجه مشكلة؟",
  previewRequestAccessNotice:
    "يُمنَح الوصول يدوياً من قِبل المشرف بعد المراجعة. لا توجد ترقية تلقائية أو دفع في هذه المرحلة.",

  appShellTitle: "هيكل بيئة العمل المُصادَق عليها",
  appShellSubtitle:
    "تم تجهيز هيكل بيئة العمل. تُفتَح شاشات الكتالوج وسير العمل التفصيلية في المرحلة التالية.",
  appShellWorkspacePending:
    "شاشات الكتالوج وسير عمل التشغيل التجريبي لم تُعرض هنا بعد. الخادم الخلفي المحمي يخدمها بالفعل.",
  appShellApiStatusHeading: "حالة API",
  appShellNavCatalog: "الكتالوج",
  appShellNavWorkflows: "سير العمل",
  appShellNavTrust: "الثقة والأمان",
  appShellNavSettings: "الإعدادات",
  appShellSectionPlaceholder:
    "يُفتَح هذا القسم في المرحلة التالية.",
  appShellComingSoonBrands: "قائمة العلامات — تُفتَح في المرحلة 1O-B.",
  appShellComingSoonProducts: "قائمة المنتجات — تُفتَح في المرحلة 1O-B.",
  appShellComingSoonProjects: "قائمة مشاريع التغليف — تُفتَح في المرحلة 1O-B.",
  appShellComingSoonAudit: "تدقيق التغليف التجريبي — يُفتَح في المرحلة 1O-B.",

  whoAmIChecking: "جارٍ التحقق من جلستك…",
  whoAmIOkLead: "تم تسجيل الدخول باسم",
  whoAmIRoles: "الأدوار",
  whoAmIPermissionsCount: "الصلاحيات",
  whoAmIAccessModeLabel: "نمط الوصول",
  whoAmIPreviewLabel: "وضع المعاينة",
  accessModeSuperAdmin: "مدير عام",
  accessModeAdmin: "مدير",
  accessModeOperator: "مشغّل",
  accessModeUser: "مستخدم",
  accessModePreview: "وصول معاينة",
  accessModeUnknown: "غير معروف",

  errAuthMissing: "سجّل الدخول للوصول إلى بيئة عمل المشغّل.",
  errAuthInvalid: "انتهت جلستك. الرجاء تسجيل الدخول مرة أخرى.",
  errPermissionDenied:
    "دورك لا يتضمّن الصلاحية المطلوبة لهذا الإجراء.",
  errRateLimited:
    "طلبات كثيرة. الرجاء الانتظار قليلاً ثم المحاولة مجدداً.",
  errServiceUnavailable:
    "خدمة Packaging OS متراجعة مؤقتاً. أعِد المحاولة بعد قليل.",
  errNetwork:
    "تعذّر الوصول إلى API الخاص بـ Packaging OS. تحقّق من اتصالك ثم حاول مرة أخرى.",
  errUnknown:
    "حدث خطأ غير متوقَّع. أعِد المحاولة، وإن استمر فأرسل معرّف الطلب إلى الدعم.",
  errRequestIdLabel: "معرّف الطلب:",

  notFoundTitle: "الصفحة غير موجودة",
  notFoundBody:
    "تعذّر العثور على الصفحة التي تبحث عنها. استخدم القائمة للعودة إلى قسم معروف.",

  catalogBrandsTitle: "العلامات التجارية",
  catalogProductsTitle: "المنتجات",
  catalogProjectsTitle: "مشاريع التغليف",
  catalogColumnName: "الاسم",
  catalogColumnSlug: "المعرّف",
  catalogColumnVersion: "الإصدار",
  catalogColumnUpdated: "آخر تحديث",
  catalogColumnCategory: "الفئة",
  catalogColumnStatus: "الحالة",
  catalogColumnMarkets: "الأسواق",
  catalogColumnLanguages: "اللغات",
  catalogEmptyTitle: "لا توجد سجلات بعد",
  catalogEmptyBody:
    "لم تُسجَّل بيانات لهذا القسم بعد. ستظهر السجلات هنا بعد أن يكمل المشرف الإعداد الأولي.",
  catalogPreviewRestrictedTitle: "وصول معاينة فقط",
  catalogPreviewRestrictedBody:
    "حسابك في وضع المعاينة ولا يملك صلاحية لعرض هذا القسم أو تشغيله. تواصل مع مشغّل المشروع لتفعيل الوصول.",
  catalogPreviewRestrictedCta: "طلب الوصول",
  catalogPaginationLabel: "الصفحة",
  catalogPrevPage: "السابقة",
  catalogNextPage: "التالية",
  catalogReadOnlyNotice:
    "في هذه المرحلة، الكتالوج متاح للعرض والمراجعة الأولية فقط. ستُفعَّل إضافة السجلات وتحريرها لاحقاً.",

  workflowDryRunTitle: "تشغيل تجربة تدقيق التغليف",
  workflowDryRunSubtitle:
    "المخرَج عبارة عن مسودة منظّمة لعين المراجع فقط. لا يستدعي المحرّك الحتمي أي مزوّد ذكاء اصطناعي في هذه المرحلة.",
  workflowDryRunFieldProductName: "اسم المنتج",
  workflowDryRunFieldProductCategory: "فئة المنتج",
  workflowDryRunFieldMarket: "السوق المستهدف",
  workflowDryRunFieldChannel: "قناة البيع",
  workflowDryRunFieldPackageType: "نوع العبوة",
  workflowDryRunFieldMaterial: "المادة المفضّلة",
  workflowDryRunFieldClaims: "الادعاءات",
  workflowDryRunFieldClaimsHelp:
    "ادعاء واحد لكل سطر. حتى 32 ادعاءً، 120 حرفاً لكل واحد.",
  workflowDryRunFieldTargetCustomer: "العميل المستهدف",
  workflowDryRunFieldLanguage: "لغة المخرَج",
  workflowDryRunFieldNotes: "ملاحظات",
  workflowDryRunSubmit: "تشغيل التدقيق التجريبي",
  workflowDryRunSubmitting: "جارٍ توليد المسودة…",
  workflowDryRunReset: "إعادة ضبط",
  workflowDryRunRequiredHint: "إلزامي",
  workflowDryRunNotSelected: "— غير محدّد —",
  workflowDryRunValidationFailed:
    "يرجى مراجعة الحقول المُعلَّمة والمحاولة مجدداً.",

  aiAssistToggleLabel: "طبقة المساعدة بالذكاء الاصطناعي",
  aiAssistToggleHint:
    "تضيف طبقة استشارية فوق المسودة الحتمية. المحرّك الحتمي يبقى مرجعاً أولاً.",
  aiAssistDraftBanner:
    "مسودة بمساعدة الذكاء الاصطناعي — تستلزم مراجعة بشرية. لا اعتماد تنظيمي ولا ضمان تجاري.",
  aiAssistStatusOk: "أُلحقت طبقة الذكاء الاصطناعي",
  aiAssistStatusFallback:
    "بوّابة الذكاء الاصطناعي غير متاحة — تُعرض المسودة الحتمية. المراجعة البشرية لا تزال لازمة.",
  aiAssistStatusOff: "مسودة حتمية فقط — المساعدة بالذكاء الاصطناعي معطّلة.",
  aiAssistAuditHeader: "سجل الذكاء الاصطناعي",

  reportInputSummary: "ملخّص الإدخال",
  reportPackagingAudit: "تدقيق التغليف",
  reportPackagingAuditPositioning: "ملاحظات التموضع",
  reportPackagingAuditPackStructure: "ملاحظات بنية العبوة",
  reportPackagingAuditShelf: "اعتبارات الرف والتوصيل",
  reportLabelCopy: "اقتراحات الملصق / النص",
  reportLabelCopyFront: "واجهة العبوة",
  reportLabelCopySide: "اللوحة الجانبية",
  reportLabelCopyTone: "ملاحظات النبرة",
  reportMaterialNotes: "ملاحظات المواد",
  reportClaimRisk: "مخاطر الادعاءات",
  reportClaimRiskLevelLabel: "مستوى الخطر",
  reportClaimRiskFlags: "العلامات",
  reportClaimRiskReviewRequired: "مراجعة مطلوبة",
  reportClaimRiskReviewReasons: "الأسباب",
  reportSupplierBrief: "مخطط بريف المورّد",
  reportLaunchKit: "مخطط حقيبة الإطلاق",
  reportTrustNotes: "ملاحظات الثقة",
  reportNextSteps: "الخطوات التالية",
  reportRiskLow: "منخفض",
  reportRiskMedium: "متوسط",
  reportRiskHigh: "مرتفع",
  reportReviewRequiredYes: "نعم",
  reportReviewRequiredNo: "لا",
  reportHaltBannerHigh:
    "تم رصد ادعاءات عالية الخطورة. أوقِف جميع أعمال التحضير حتى يعتمد مراجع مخاطر الادعاءات ومستشار تنظيمي. لا تقم بالتصدير.",
  reportHaltBannerMedium:
    "تم رصد صياغة ادعاء حسّاسة. يجب أن يعتمد مراجع مؤهَّل النص قبل التصدير.",
  reportNoFlags: "لا توجد علامات. مراجعة بشرية غير مطلوبة عند هذا المستوى.",

  intakeTitle: "استلام بريف المنتج والعلامة",
  intakeSubtitle:
    "التقط بريفاً منظَّماً للمنتج والعلامة والسوق والهدف والقيود والأصول. يصدر المحرّك الحتمي مسوّدة هيكلية للمراجع فقط — لا يعمل أي مزوّد ذكاء اصطناعي في هذه المرحلة.",
  intakeGroupProduct: "أساسيات المنتج",
  intakeGroupBrand: "سياق العلامة",
  intakeGroupMarket: "السوق والقناة",
  intakeGroupObjective: "هدف التغليف",
  intakeGroupConstraints: "القيود",
  intakeGroupAssets: "الأصول المتاحة",
  intakeFieldProductName: "اسم المنتج",
  intakeFieldProductCategory: "فئة المنتج",
  intakeFieldProductSubcategory: "الفئة الفرعية",
  intakeFieldProductDescription: "وصف المنتج",
  intakeFieldProductForm: "شكل المنتج",
  intakeFieldSizeValue: "قيمة الحجم",
  intakeFieldSizeUnit: "وحدة الحجم",
  intakeFieldPriceBand: "شريحة السعر",
  intakeFieldBrandName: "اسم العلامة",
  intakeFieldBrandPersonality: "شخصية العلامة",
  intakeFieldBrandMaturity: "نضج العلامة",
  intakeFieldCurrentPackagingStatus: "حالة التغليف الحالي",
  intakeFieldVisualPreferences: "التفضيلات البصرية",
  intakeFieldToneOfVoice: "نبرة الصوت",
  intakeFieldTargetMarkets: "الأسواق المستهدفة",
  intakeFieldTargetMarketsHelp:
    "رموز ISO-2 مفصولة بفواصل (مثلاً AE، SA، TR). حتى 8.",
  intakeFieldTargetLanguages: "اللغات المستهدفة",
  intakeFieldSalesChannels: "قنوات البيع",
  intakeFieldTargetAudience: "الجمهور المستهدف",
  intakeFieldObjectives: "الأهداف",
  intakeFieldObjectivesHelp: "اختر واحداً على الأقل. يسمح بعدة أهداف.",
  intakeFieldMaterialConstraints: "قيود المواد",
  intakeFieldBudgetBand: "شريحة الميزانية",
  intakeFieldProductionConstraints: "قيود الإنتاج",
  intakeFieldSustainabilityPreferences: "تفضيلات الاستدامة",
  intakeFieldSustainabilityPreferencesHelp:
    "وسوم قصيرة، واحد في كل سطر. حتى 8.",
  intakeFieldClaimsToAvoid: "ادعاءات يجب تجنّبها",
  intakeFieldClaimsToAvoidHelp:
    "ادعاء واحد لكل سطر. حتى 32، 120 حرفاً لكل واحد.",
  intakeFieldAssetsAvailable: "الأصول المتاحة",
  intakeFieldLanguage: "لغة المخرَج",
  intakeFieldNotes: "ملاحظات",
  intakeSubmit: "تشغيل استلام بريف المنتج",
  intakeSubmitting: "جارٍ توليد المسودة…",
  intakeReset: "إعادة ضبط",
  intakeReportProductBrief: "ملخّص بريف المنتج",
  intakeReportBrandContext: "ملخّص سياق العلامة",
  intakeReportObjectiveSummary: "ملخّص هدف التغليف",
  intakeReportMarketChannelFit: "ملاحظات ملاءمة السوق والقناة",
  intakeReportMissingChecklist: "قائمة المعلومات الناقصة",
  intakeReportAssumptionLog: "سجل الفرضيات",
  intakeReportInitialRiskFlags: "علامات الخطر الأولية",
  intakeReportHumanReview: "ملاحظات تتطلّب مراجعة بشرية",
  intakeReportRecommendedNextEngines: "محرّكات لاحقة موصى بها",

  intakeReportStrategy: "ملخّص استراتيجية التغليف",
  intakeReportTargetCustomer: "تفسير العميل المستهدف",
  intakeReportPositioning: "ملاحظات التموضع",
  intakeReportChannelReadiness: "ملاحظات جاهزية القنوات",
  intakeReportLocalization: "ملاحظات اللغة والتعريب",
  intakeReportDecisionPriorities: "أولويات قرار التغليف",
  intakeReportOwnerQuestions: "أسئلة المالك للحلّ",
  intakeNextLabelCopy: "التالي: مراجعة الملصقات والادعاءات",

  labelCopyTitle: "مراجعة الملصقات والادعاءات",
  labelCopySubtitle:
    "فحص حتمي لمخاطر الادعاءات + هيكل نص ملصق. لا يُعاد كتابة الادعاءات الخطرة بصياغة معتمدة. المحرّك لا يصدر استشهاداً تنظيمياً.",
  labelCopyFieldProductName: "اسم المنتج",
  labelCopyFieldCategory: "فئة المنتج",
  labelCopyFieldBrandName: "اسم العلامة",
  labelCopyFieldTargetMarket: "السوق المستهدفة (ISO-2)",
  labelCopyFieldTargetLanguage: "اللغة المستهدفة",
  labelCopyFieldSalesChannel: "قناة البيع",
  labelCopyFieldProductDescription: "وصف المنتج",
  labelCopyFieldPackagingObjective: "هدف التغليف",
  labelCopyFieldClaimDrafts: "مسودات الادعاء/النص",
  labelCopyFieldClaimDraftsHelp:
    "ادعاء واحد لكل سطر. حتى 32، 200 حرفاً لكل واحد.",
  labelCopyFieldToneOfVoice: "نبرة الصوت",
  labelCopyFieldClaimsToAvoid: "ادعاءات يجب تجنّبها",
  labelCopyFieldRequiredLabelElements: "عناصر ملصق إلزامية",
  labelCopyFieldRequiredLabelElementsHelp:
    "عنصر واحد لكل سطر. حتى 12.",
  labelCopySubmit: "تشغيل مراجعة الملصقات والادعاءات",
  labelCopySubmitting: "جارٍ توليد المسودة…",
  labelCopyReset: "إعادة ضبط",
  labelCopyReportFront: "مسودة الواجهة",
  labelCopyReportBack: "مسودة الخلفية",
  labelCopyReportStory: "قصة المنتج القصيرة",
  labelCopyReportInstructions: "تعليمات الاستخدام/التقديم/المناولة",
  labelCopyReportWarning: "تحذير/تنبيه",
  labelCopyReportClaimRiskTable: "جدول مخاطر الادعاءات",
  labelCopyReportSaferWording: "اقتراحات صياغة آمنة",
  labelCopyReportMissingInfo: "معلومات الملصق الناقصة",
  labelCopyReportHumanReview: "مراجعة بشرية لازمة",
  labelCopyReportCopyQuality: "ملاحظات جودة النص",
  labelCopyClaimColClaim: "الادعاء",
  labelCopyClaimColSeverity: "الشدّة",
  labelCopyClaimColCategory: "الفئة",
  labelCopyClaimColMatched: "المصطلحات المتطابقة",
  labelCopyClaimColReview: "مراجعة",

  materialTitle: "المادة والبنية",
  materialSubtitle:
    "اقتراح حتمي لصيغة التغليف / المادة / البنية. لا ضمان هندسي أو إنتاجي. مراجعة بشرية لازمة.",
  materialFieldFragility: "الهشاشة",
  materialFieldTemperatureSensitive: "حساس للحرارة",
  materialFieldSustainabilityPreferences: "تفضيلات الاستدامة",
  materialReportFormat: "صيغة التغليف الموصى بها",
  materialReportMaterial: "اقتراح المادة",
  materialReportStructure: "اقتراح البنية",
  materialReportDurability: "المتانة / المناولة",
  materialReportDelivery: "ملاءمة التوصيل",
  materialReportEcommerce: "ملاءمة التجارة الإلكترونية",
  materialReportShelf: "ملاءمة الرف",
  materialReportProduction: "جدوى الإنتاج",
  materialReportCost: "حساسية التكلفة",
  materialReportSupplierQuestions: "أسئلة مفتوحة للمورّد",
  materialSubmit: "تشغيل المادة والبنية",
  materialSubmitting: "جارٍ توليد المسودة…",
  materialReset: "إعادة ضبط",
  intakeNextMaterial: "التالي: المادة والبنية",

  sustTitle: "مخاطر الاستدامة والامتثال",
  sustSubtitle:
    "فحص حتمي للغسل الأخضر / الاستدامة / الامتثال. لا يصدر المحرّك استشهاداً تنظيمياً ولا يعتمد ادعاءً.",
  sustFieldPackagingMaterial: "مادة التغليف",
  sustFieldClaims: "ادعاءات الاستدامة",
  sustFieldClaimsHelp: "ادعاء واحد لكل سطر. حتى 32، 200 حرفاً لكل واحد.",
  sustFieldLabelDrafts: "مسودات الملصق/النص (اختياري)",
  sustFieldRecyclable: "يوجد ادعاء قابلية التدوير",
  sustFieldBiodegradable: "يوجد ادعاء قابلية التحلل الحيوي",
  sustFieldCompostable: "يوجد ادعاء قابلية التحلل في السماد",
  sustFieldOrigin: "يوجد ادعاء منشأ / صُنع في",
  sustFieldBarcode: "باركود حاضر على الملصق",
  sustFieldExpiry: "صلاحية / أفضل قبل حاضر",
  sustFieldOriginLabel: "ملصق بلد المنشأ حاضر",
  sustFieldWarning: "تحذيرات إلزامية حاضرة",
  sustReportClaimReview: "مراجعة ادعاء الاستدامة",
  sustReportGreenwashing: "علامات مخاطر الغسل الأخضر",
  sustReportSeverityTable: "جدول شدّة الادعاءات",
  sustReportSafer: "صياغة استدامة آمنة",
  sustReportCompliance: "قائمة تذكير الامتثال",
  sustReportCountry: "ملاحظات البلد / المنطقة",
  sustReportEvidence: "الأدلة المطلوبة",
  sustReportNotApproved: "مسودة — غير معتمد",
  sustSubmit: "تشغيل مخاطر الاستدامة والامتثال",
  sustSubmitting: "جارٍ توليد المسودة…",
  sustReset: "إعادة ضبط",
  sustColTerm: "المصطلح",
  sustColSeverity: "الشدّة",
  sustColCategory: "الفئة",
  sustColEvidence: "أدلة مطلوبة",
  labelCopyNextSust: "التالي: مخاطر الاستدامة والامتثال",

  supplierTitle: "بريف المورّد / RFQ",
  supplierSubtitle:
    "بريف مورّد ومسودة RFQ حتمية. المحرّك لا يُرسل أبداً، ولا يختار مورّداً، ولا يدّعي سعراً نهائياً أو اعتماداً تصنيعياً.",
  supplierFieldFormat: "صيغة التغليف",
  supplierFieldMaterial: "مادة التغليف",
  supplierFieldStructure: "اقتراح البنية",
  supplierFieldQuantity: "نطاق الكمية",
  supplierFieldPrintFinish: "الطباعة / الإنهاء",
  supplierFieldKnownConstraints: "قيود معروفة",
  supplierFieldQuestions: "أسئلة للمورّد",
  supplierFieldQuestionsHelp: "واحد لكل سطر. حتى 12.",
  supplierReportBrief: "ملخّص بريف المورّد",
  supplierReportSpec: "مسودة مواصفات التغليف",
  supplierReportMaterial: "متطلبات المادة / البنية",
  supplierReportPrint: "متطلبات الطباعة / الإنهاء",
  supplierReportQuantity: "أسئلة الكمية / MOQ",
  supplierReportLead: "أسئلة المهلة",
  supplierReportQuality: "أسئلة الجودة / الاختبار",
  supplierReportSust: "أسئلة الاستدامة / الأدلة",
  supplierReportRfqEmail: "مسودة بريد RFQ (المحرّك لا يرسل)",
  supplierReportCompare: "معايير مقارنة المورّدين",
  supplierReportMissing: "معلومات إنتاجية ناقصة",
  supplierSubmit: "تشغيل بريف المورّد / RFQ",
  supplierSubmitting: "جارٍ توليد المسودة…",
  supplierReset: "إعادة ضبط",
  materialNextSupplier: "التالي: بريف المورّد / RFQ",
  sustNextSupplier: "التالي: بريف المورّد / RFQ (طلب أدلة)",

  visualTitle: "الاتجاه البصري",
  visualSubtitle:
    "بريف اتجاه بصري حتمي. المحرّك لا يولّد صورة، ولا ينسخ فنّ منافس، ولا يعتمد فناً نهائياً.",
  visualFieldBrandPersonality: "شخصية العلامة",
  visualFieldTargetCustomer: "العميل المستهدف",
  visualFieldVisualPreference: "تفضيل بصري",
  visualFieldColorsToUse: "ألوان لاستخدامها",
  visualFieldColorsToAvoid: "ألوان لتجنّبها",
  visualFieldTypographyPreference: "تفضيل الطباعة",
  visualFieldCompetitorReferences: "مراجع المنافسين",
  visualFieldCompetitorReferencesHelp:
    "واحد لكل سطر. للإلهام فقط — لا يُنسَخ أبداً.",
  visualReportSummary: "ملخّص الاتجاه البصري",
  visualReportRoutes: "خيارات مسار التصميم",
  visualReportColor: "اتجاه الألوان",
  visualReportTypography: "اتجاه الطباعة",
  visualReportLayout: "اتجاه التخطيط",
  visualReportSurface: "أولوية أسطح التغليف",
  visualReportShelfEc: "ملاحظات بصرية للرف / التجارة الإلكترونية",
  visualReportCultural: "ملاحظات ثقافية / تعريب",
  visualReportMockup: "حزمة موجِّه نموذج",
  visualReportHandoff: "ملاحظات تسليم للمصمم",
  visualReportRisks: "مخاطر بصرية / تجنّب",
  visualSubmit: "تشغيل الاتجاه البصري",
  visualSubmitting: "جارٍ توليد المسودة…",
  visualReset: "إعادة ضبط",
  intakeNextVisual: "التالي: الاتجاه البصري",
  labelCopyNextVisual: "التالي: الاتجاه البصري",

  scorecardTitle: "بطاقة تقييم التغليف والباقة العمودية",
  scorecardSubtitle:
    "تقييم استكشافي حتمي 0–100 للرف / التجارة الإلكترونية / التوصيل / فتح العبوة / الأسواق، مع تأثير الباقة العمودية المختارة. لا تحليل صور. مراجعة بشرية لازمة.",
  scorecardFieldVerticalPack: "الباقة العمودية",
  scorecardFieldShelfEnv: "بيئة الرف",
  scorecardFieldFragility: "الهشاشة",
  scorecardFieldLiquid: "سائل",
  scorecardFieldTempSensitive: "حساس للحرارة",
  scorecardFieldThumbnailOpt: "صورة مصغّرة لتجارة إلكترونية محسّنة",
  scorecardFieldProtectiveInsert: "حشوة حماية",
  scorecardFieldLeakSeal: "ختم مقاوم للتسرّب",
  scorecardFieldDropTest: "اختبار سقوط ناجح",
  scorecardFieldVisualNotes: "ملاحظات الاتجاه البصري",
  scorecardOverall: "النتيجة الكلية للتغليف",
  scorecardSectionShelf: "الظهور على الرف",
  scorecardSectionEcommerce: "جاهزية الصورة المصغّرة",
  scorecardSectionDelivery: "متانة التوصيل",
  scorecardSectionUnboxing: "تجربة فتح العبوة",
  scorecardSectionMarketplace: "جاهزية الأسواق الإلكترونية",
  scorecardVerticalPackLabel: "الباقة العمودية",
  scorecardRiskFlags: "علامات المخاطر",
  scorecardImprovements: "اقتراحات التحسين",
  scorecardChannelRecs: "توصيات حسب القناة",
  scorecardMissing: "معلومات ناقصة",
  scorecardSubmit: "تشغيل بطاقة تقييم التغليف",
  scorecardSubmitting: "جارٍ توليد المسودة…",
  scorecardReset: "إعادة ضبط",
  scorecardBandHigh: "مرتفع",
  scorecardBandMedium: "متوسط",
  scorecardBandLow: "منخفض",
  visualNextScorecard: "التالي: بطاقة تقييم التغليف",
  supplierNextScorecard: "التالي: بطاقة تقييم التغليف",
  launchKitTitle: "حقيبة الإطلاق",
  launchKitSubtitle: "مسودات نص حتمية للإطلاق لمراجعة بشرية. المحرّك لا يَنشر أبداً ولا يدّعي اعتماداً نهائياً.",
  launchKitFieldPositioning: "تموضع التغليف",
  launchKitFieldKeyBenefit: "الفائدة الرئيسة",
  launchKitFieldClaimRestrictions: "ادعاءات مقيّدة",
  launchKitFieldQrConnected: "QR / تغليف متصل",
  launchKitFieldLaunchChannels: "قنوات الإطلاق",
  launchKitReportPositioning: "ملخّص تموضع الإطلاق",
  launchKitReportListing: "مسودة قائمة التجارة الإلكترونية",
  launchKitReportSocial: "مسودات محتوى وسائل التواصل",
  launchKitReportInsert: "نص بطاقة insert",
  launchKitReportQr: "فكرة QR / متصل",
  launchKitReportLoyalty: "نصوص مراجعة / إعادة شراء / ولاء",
  launchKitReportSafety: "ملاحظات سلامة الادعاءات",
  launchKitSubmit: "تشغيل حقيبة الإطلاق",
  launchKitSubmitting: "جارٍ توليد المسودة…",
  launchKitReset: "إعادة ضبط",
  postLaunchTitle: "تحليل ما بعد الإطلاق",
  postLaunchSubtitle: "نص ملصق من المشغّل فقط. لا scraping خارجي، لا API أسواق، لا PII، لا ادعاء إحصائي.",
  postLaunchFieldReviews: "مقتطفات المراجعات (سطر واحد لكل)",
  postLaunchFieldReturns: "أسباب الإرجاع (سطر واحد لكل)",
  postLaunchFieldFeedback: "ملاحظات العملاء (سطر واحد لكل)",
  postLaunchFieldPerformance: "ملاحظات أداء التجارة الإلكترونية",
  postLaunchFieldVersion: "إصدار التغليف",
  postLaunchReportThemes: "ملخّص محاور الملاحظات",
  postLaunchReportComplaints: "تصنيف شكاوى التغليف",
  postLaunchReportReturns: "تصنيف أسباب الإرجاع / التلف",
  postLaunchReportSentiment: "ملاحظات نبرة المراجعات",
  postLaunchReportImprovements: "اقتراحات تحسين التغليف",
  postLaunchReportNext: "أولويات الإصدار التالي",
  postLaunchReportDataQuality: "جودة البيانات / البيانات الناقصة",
  postLaunchSubmit: "تشغيل تحليل ما بعد الإطلاق",
  postLaunchSubmitting: "جارٍ توليد المسودة…",
  postLaunchReset: "إعادة ضبط",
  reviewChecklistTitle: "قائمة المراجعة",
  reviewChecklistSubtitle: "قائمة تحقّق حتمية + قالب مراجعة المخاطر + جاهزية التصدير. لا DB، لا تخزين، لا اعتماد نهائي.",
  reviewChecklistFieldTarget: "هدف سير العمل",
  reviewChecklistFieldHighRisk: "ادعاء عالي الخطورة موجود",
  reviewChecklistFieldEvidenceGap: "فجوة أدلة موجودة",
  reviewChecklistFieldSustClaim: "ادعاء استدامة غير مدعوم",
  reviewChecklistFieldFoodContact: "قلق التماس الغذائي",
  reviewChecklistFieldCosmetic: "قلق سلامة المستحضرات",
  reviewChecklistFieldIndustrial: "قلق المخاطر الصناعية",
  reviewChecklistFieldSupplier: "عدم يقين المورّد",
  reviewChecklistReportChecklist: "قائمة المراجعة",
  reviewChecklistReportCriteria: "معايير الاعتماد",
  reviewChecklistReportRisks: "بنود مراجعة المخاطر",
  reviewChecklistReportEvidence: "أدلة مطلوبة",
  reviewChecklistReportExport: "جاهزية التصدير",
  reviewChecklistReportDecision: "قالب قرار المراجع",
  reviewChecklistSubmit: "تشغيل قائمة المراجعة",
  reviewChecklistSubmitting: "جارٍ توليد المسودة…",
  reviewChecklistReset: "إعادة ضبط",
  servicesTitle: "الخدمات وحزم ذكاء التغليف",
  servicesIntro:
    "يساعد Packaging OS الفرق على اتخاذ قرارات تغليف أفضل قبل الإنفاق على التصميم أو الطباعة أو الموردين أو الادعاءات أو الإطلاق. كل مخرج هو مسودة دعم قرار.",
  servicesHumanReview: "مراجعة بشرية مطلوبة لكل مخرج",
  servicesNotLegal: "ليست موافقة قانونية أو تنظيمية",
  servicesNoBilling: "لا يوجد دفع إلكتروني — يبدأ التعامل بطلب",
  packPilotReady: "طلبات التجربة مفتوحة",
  packFoundation: "أساس تجاري",
  packKnownGaps: "الفجوات المعروفة",
  packWorkflows: "مسارات العمل المشمولة",
  packRequestQuote: "اطلب عرض سعر",
  pricingTitle: "الباقات والأسعار",
  pricingIntro:
    "تُحدَّد الأسعار لكل تعاقد على حدة. لا يوجد دفع أو اشتراك إلكتروني — أرسل طلباً وسيتواصل معك فريقنا.",
  pricingDraftLabel: "تسعير مبدئي — اطلب عرض سعر",
  requestTitle: "اطلب وصولاً أو عرضاً توضيحياً أو تدقيقاً أو تجربة",
  requestIntro:
    "أخبرنا عن منتجك وسوقك. تُحفَظ الطلبات ويراجعها إنسان — لا تُرسَل رسائل آلية.",
  requestTypeLabel: "ماذا تحتاج؟",
  requestTypeAccess: "الوصول إلى المنصة",
  requestTypeDemo: "عرض توضيحي",
  requestTypeAudit: "تدقيق التغليف",
  requestTypePilot: "مشروع تجريبي",
  requestNameLabel: "اسمك",
  requestEmailLabel: "البريد الإلكتروني",
  requestCompanyLabel: "الشركة / العلامة",
  requestCountryLabel: "الدولة / السوق",
  requestLanguageLabel: "اللغة المفضلة",
  requestMessageLabel: "رسالة (اختياري)",
  requestSubmit: "إرسال الطلب",
  requestSubmitting: "جارٍ الإرسال…",
  requestSuccess: "تم حفظ طلبك وسيراجعه الفريق ثم يتواصل معك.",
  requestNoEmailNote: "لا يُرسَل بريد آلي في هذه المرحلة.",
  requestValidationError: "يرجى إدخال اسم وبريد إلكتروني صحيحين.",
  adminTitle: "وحدة تحكم المشرف",
  adminOverviewTab: "نظرة عامة",
  adminReviewOpsTab: "عمليات المراجعة",
  adminCommercialTab: "تجاري",
  adminSupportTab: "الدعم",
  adminBlockedMessage: "تتطلب هذه الوحدة صلاحيات المالك/المشرف.",
  adminCardUsers: "المستخدمون",
  adminCardRuns: "تشغيلات المسارات",
  adminCardReview: "مستندات بانتظار المراجعة",
  adminCardGaps: "فجوات المصادر المفتوحة",
  adminCardKnowledge: "ملفات المعرفة",
  adminCardCost: "إجمالي التكلفة (دولار)",
  adminCardRequests: "طلبات تجارية جديدة",
  adminAiModeLabel: "وضع الذكاء الاصطناعي",
  adminBillingDisabled: "الفوترة معطلة — لا يوجد مزود دفع",
  runSavedBanner: "تم حفظ التشغيل — أُنشئ مستند مسودة (مطلوبة مراجعة بشرية)",
  runSavedArtifactLink: "فتح المستند",
  navArtifacts: "المستندات والتصدير",
  navKnowledge: "المعرفة",
  navAdmin: "وحدة تحكم المشرف",
  artifactsTitle: "المستندات والتصدير",
  artifactsIntro: "كل تشغيل محفوظ يُنشئ مستند مسودة. راجِع واعتمد وصدِّر هنا — التصدير النهائي يبقى مشروطاً بالاعتماد.",
  artifactStatusLabel: "الحالة",
  artifactReviewApprove: "اعتماد",
  artifactReviewReject: "رفض",
  artifactExportDraft: "تصدير مسودة (MD)",
  artifactExportFinal: "تصدير نهائي (PDF)",
  artifactExportFinalGated: "التصدير النهائي يتطلب مستنداً معتمداً.",
  artifactDownload: "تنزيل",
  artifactSourceGaps: "فجوات المصادر المفتوحة",
  artifactEvidence: "الأدلة المرتبطة",
  artifactVersions: "الإصدارات",
  knowledgeTitle: "دماغ المعرفة",
  knowledgeIntro: "ارفع مستندات مرجعية (PDF/DOCX/TXT/CSV/XLSX). المحتوى المرفوع ليس حقيقة — تبقى تحذيرات الاستخراج ظاهرة وتحتاج الأدلة تحققاً بشرياً.",
  knowledgeUploadLabel: "ملف المستند",
  knowledgeLanguageHint: "لغة المصدر (مصرّح بها)",
  knowledgeUploadButton: "رفع",
  knowledgeExtractButton: "استخراج",
  knowledgeSearchLabel: "ابحث في معرفة مساحة العمل",
  knowledgeSearchButton: "بحث",
  knowledgeWarnings: "تحذيرات الاستخراج",
  knowledgeNoOcrNote: "ملفات PDF الممسوحة ضوئياً غير مدعومة (لا OCR) — تفشل بصدق مع تحذير.",
};

const fa: UiStrings = {
  appName: "Packaging OS",
  switchLanguageLabel: "تغییر زبان",
  copyrightLine: "© 2026 Packaging OS",

  trustBannerFull:
    "این یک سطح پیش‌نویس است. خروجی نهایی یا تجاری نیست. هیچ ضمانت قانونی وجود ندارد. ارائه‌دهندگان هوش مصنوعی در این فاز فعال نیستند.",
  trustBannerShort: "سطح پیش‌نویس. بدون ضمانت قانونی.",
  trustNotesSourceBacked: "مستند به منبع: خیر",
  trustNotesRegulatoryGuarantee: "ضمانت قانونی: خیر",
  trustNotesFinalOutput: "خروجی نهایی: خیر",

  ctaSignInWithGoogle: "ادامه با Google",
  ctaRequestAccess: "درخواست دسترسی",
  ctaOpenDashboard: "ورود به فضای کاری",
  ctaSignOut: "خروج",
  ctaBackToLanding: "بازگشت به صفحه اصلی",

  landingHeroTitle:
    "سامانه هوش بسته‌بندی و تجربه محصول",
  landingHeroSubtitle:
    "یک فضای کاری حرفه‌ای کنترل‌شده برای ممیزی بسته‌بندی، بازبینی برچسب و متن، یادداشت‌های مواد، ریسک ادعا، و پیش‌نویس بریف تأمین‌کننده. هدایت‌شده توسط اپراتورها. اعتماد در اولویت.",
  landingTrustStripTitle: "وضعیت فعلی محصول",
  landingTrustStripDraftItem:
    "فقط پیش‌نویس — هیچ خروجی نهایی یا تجاری از این سطح ارائه نمی‌شود.",
  landingTrustStripNoLiveAiItem:
    "ارائه‌دهندگان هوش مصنوعی در این فاز فعال نیستند. خروجی موتور قطعی و مبتنی بر قاعده است.",
  landingTrustStripControlledAccessItem:
    "دسترسی توسط مدیر کنترل می‌شود. ثبت‌نام خودسرویس وجود ندارد.",
  landingCapabilityHeading: "آن‌چه پشت دروازه قرار دارد",
  landingCapabilityCatalogTitle: "کاتالوگ",
  landingCapabilityCatalogBody:
    "برندها، محصولات و پروژه‌های بسته‌بندی برای بازبینی سازمان‌دهی شده‌اند، با نظم نسخه‌گذاری و رکورد فعال.",
  landingCapabilityWorkflowsTitle: "گردش‌کارها",
  landingCapabilityWorkflowsBody:
    "ممیزی بسته‌بندی به‌صورت dry-run یک پیش‌نویس ساختاریافته برای بازبین‌ها تولید می‌کند، با پرچم‌های ریسک ادعا و طرح بریف تأمین‌کننده.",
  landingCapabilityTrustTitle: "اعتماد و ایمنی",
  landingCapabilityTrustBody:
    "هر خروجی وضعیت صریحی دارد: پیش‌نویس / منبع / قانونی / نیازمند بازبینی. تأیید بازبین انسانی پیش از خروجی نهایی الزامی است.",
  landingWorkflowPreviewHeading: "نحوه کار جریان ممیزی",
  landingWorkflowPreviewBody:
    "اپراتورها یک بریف بسته‌بندی ارسال می‌کنند — محصول، بازار، کانال، ادعاها. موتور قطعی یک پیش‌نویس ممیزی، یادداشت‌های برچسب و متن، یادداشت‌های مواد، ریسک ادعا، طرح بریف تأمین‌کننده و طرح کیت عرضه برمی‌گرداند. هیچ‌چیز پیش از تأیید بازبین نهایی نیست.",
  landingAccessHeading: "مدل دسترسی",
  landingAccessBody:
    "نقش‌ها توسط مدیر در دروازه هویت پلتفرم تخصیص می‌یابند. حساب‌های پیش‌نمایش فقط کاتالوگ را مرور می‌کنند؛ ویرایشگرها و صاحبان برند می‌توانند ممیزی dry-run اجرا کنند؛ بازبین‌ها تأیید می‌کنند. بدون ارتقای خودسرویس.",
  landingCtaSectionHeading: "آماده ورود هستید؟",
  landingCtaSectionBody:
    "اگر نقشی برای شما تعیین شده، وارد شوید. در غیر این صورت، درخواست دسترسی دهید — هر درخواست به‌صورت دستی توسط مدیر بررسی می‌شود.",
  landingFooterEcosystem:
    "Packaging OS یک اپ عمودی روی اکوسیستم ai-platform-core است. هویت، ممیزی و اجرای هوش مصنوعی به Core تفویض می‌شود.",

  loginTitle: "ورود به Packaging OS",
  loginGoogleHelperText:
    "ورود از طریق دروازه هویت Core انجام می‌شود. Packaging OS هرگز رمز عبور شما را نمی‌بیند.",
  loginEmailComingSoonLabel: "ایمیل و رمز عبور — به‌زودی",
  loginEmailComingSoonTooltip:
    "ورود با ایمیل و رمز عبور در این فاز فعال نیست. به یک حساب از پیش تخصیص‌داده‌شده در ai-platform-core نیاز داریم.",
  loginRequestAccessLead: "هنوز دسترسی ندارید؟",
  loginTrustParagraph:
    "نقش شما توسط مدیر تخصیص می‌یابد. ورود فقط هویت را تأیید می‌کند و دسترسی اضافه نمی‌دهد.",
  loginCoreUrlMissingNotice:
    "آدرس ورود Core در این build پیکربندی نشده است. اپراتور باید VITE_CORE_AUTH_START_URL را پیش از در دسترس بودن ورود تنظیم کند.",
  loginEmailLabel: "ایمیل",
  loginPasswordLabel: "رمز عبور",
  loginSubmitLabel: "ورود",
  loginSubmittingLabel: "در حال ورود…",
  loginErrorInvalidCredentials: "ایمیل یا رمز عبور نادرست است.",
  loginErrorSuspended:
    "این حساب معلق شده است. لطفاً با مدیر پلتفرم تماس بگیرید.",
  loginErrorUnavailable:
    "ورود موقتاً در دسترس نیست. لطفاً دوباره تلاش کنید.",
  loginGoogleUnavailableNotice:
    "ورود با Google هنوز در دسترس نیست. از ایمیل و رمز عبور استفاده کنید.",
  loginPreviewModeNotice:
    "حساب شما در حالت پیش‌نمایش است. برای فعال‌سازی دسترسی کامل، لطفاً با مدیر پلتفرم تماس بگیرید.",
  loginPreviewContinue: "ادامه در حالت پیش‌نمایش",
  loginErrorOauthCancelled:
    "ورود لغو شد. می‌توانید دوباره تلاش کنید یا درخواست دسترسی دهید.",
  loginErrorOauthFailed:
    "ورود کامل نشد. لطفاً دوباره تلاش کنید. اگر مشکل ادامه داشت با مدیر تماس بگیرید.",
  loginErrorServiceDown:
    "در حال حاضر دسترسی به سرویس ورود ممکن نیست. کمی بعد دوباره تلاش کنید.",
  loginPostauthChecking: "در حال تکمیل ورود…",
  loginPostauthSessionMissing:
    "ورود انجام شد، اما جلسه Packaging هنوز در دسترس نیست. دوباره تلاش کنید یا با اپراتور تماس بگیرید.",
  loginPostauthAccessDenied:
    "ورود موفق بود، اما حساب شما به این فضای کاری دسترسی ندارد. با اپراتور تماس بگیرید.",
  loginPostauthUnavailable:
    "تأیید جلسه ممکن نشد. کمی بعد دوباره تلاش کنید.",
  loginPostauthContinue: "ورود به فضای کاری",

  previewTitle: "شما در حالت پیش‌نمایش هستید.",
  previewBanner:
    "کاتالوگ فقط-خواندنی است و گردش‌کار ممیزی پشت یک نقش تخصیص‌یافته توسط مدیر قرار دارد.",
  previewYouCanHeading: "آن‌چه می‌توانید انجام دهید",
  previewYouCanItem1: "خلاصه دسترسی و نقش‌های خود را ببینید.",
  previewYouCanItem2: "صفحه عمومی و وضعیت اعتماد را بخوانید.",
  previewLockedHeading: "آن‌چه قفل است",
  previewLockedAuditReason:
    "اجرای ممیزی بسته‌بندی — نیازمند مجوز packaging.workflow.start.",
  previewLockedExportReason:
    "خروجی گرفتن از نتایج — فاز ۲، در انتظار اجرای واقعی گردش‌کار.",
  previewLockedReviewReason:
    "ارسال به بازبین — نیازمند نقش بازبین.",
  previewLockedSupplierReason:
    "ساخت بریف کامل تأمین‌کننده — فاز ۲، در انتظار اجرای واقعی گردش‌کار.",
  previewContactAdminLead: "مشکل دارید؟",
  previewRequestAccessNotice:
    "دسترسی توسط مدیر و پس از بررسی به‌صورت دستی اعطا می‌شود. ارتقای خودکار یا پرداخت در این فاز وجود ندارد.",

  appShellTitle: "پوسته فضای کاری احراز‌شده",
  appShellSubtitle:
    "پوسته فضای کاری آماده شده است. صفحه‌های کاتالوگ و گردش‌کار جزئی در فاز بعدی باز می‌شوند.",
  appShellWorkspacePending:
    "صفحه‌های کاتالوگ و گردش‌کار dry-run هنوز اینجا رندر نمی‌شوند. بک‌اند محافظت‌شده آن‌ها را در حال حاضر سرو می‌کند.",
  appShellApiStatusHeading: "وضعیت API",
  appShellNavCatalog: "کاتالوگ",
  appShellNavWorkflows: "گردش‌کارها",
  appShellNavTrust: "اعتماد و ایمنی",
  appShellNavSettings: "تنظیمات",
  appShellSectionPlaceholder:
    "این بخش در فاز بعدی باز می‌شود.",
  appShellComingSoonBrands: "لیست برندها — در فاز 1O-B باز می‌شود.",
  appShellComingSoonProducts: "لیست محصولات — در فاز 1O-B باز می‌شود.",
  appShellComingSoonProjects: "لیست پروژه‌های بسته‌بندی — در فاز 1O-B باز می‌شود.",
  appShellComingSoonAudit: "ممیزی بسته‌بندی dry-run — در فاز 1O-B باز می‌شود.",

  whoAmIChecking: "در حال بررسی نشست شما…",
  whoAmIOkLead: "وارد شده به‌عنوان",
  whoAmIRoles: "نقش‌ها",
  whoAmIPermissionsCount: "مجوزها",
  whoAmIAccessModeLabel: "نوع دسترسی",
  whoAmIPreviewLabel: "حالت پیش‌نمایش",
  accessModeSuperAdmin: "مدیر ارشد",
  accessModeAdmin: "مدیر",
  accessModeOperator: "اپراتور",
  accessModeUser: "کاربر",
  accessModePreview: "دسترسی پیش‌نمایش",
  accessModeUnknown: "نامشخص",

  errAuthMissing: "برای دسترسی به فضای کاری اپراتور وارد شوید.",
  errAuthInvalid: "نشست شما منقضی شد. لطفاً دوباره وارد شوید.",
  errPermissionDenied:
    "نقش شما مجوز لازم برای این عمل را ندارد.",
  errRateLimited:
    "درخواست‌ها زیاد است. لحظه‌ای صبر کنید و دوباره تلاش کنید.",
  errServiceUnavailable:
    "سرویس Packaging OS موقتاً افت کرده است. کمی بعد دوباره تلاش کنید.",
  errNetwork:
    "نتوانستیم به API بسته‌بندی متصل شویم. اتصال خود را بررسی و دوباره تلاش کنید.",
  errUnknown:
    "خطای پیش‌بینی‌نشده. دوباره تلاش کنید و در صورت تکرار شناسه درخواست را به پشتیبانی بدهید.",
  errRequestIdLabel: "شناسه درخواست:",

  notFoundTitle: "صفحه پیدا نشد",
  notFoundBody:
    "صفحه‌ای که دنبال آن بودید پیدا نشد. از منوی ناوبری برای بازگشت به یک بخش مشخص استفاده کنید.",

  catalogBrandsTitle: "برندها",
  catalogProductsTitle: "محصولات",
  catalogProjectsTitle: "پروژه‌های بسته‌بندی",
  catalogColumnName: "نام",
  catalogColumnSlug: "شناسه",
  catalogColumnVersion: "نسخه",
  catalogColumnUpdated: "آخرین به‌روزرسانی",
  catalogColumnCategory: "دسته",
  catalogColumnStatus: "وضعیت",
  catalogColumnMarkets: "بازارها",
  catalogColumnLanguages: "زبان‌ها",
  catalogEmptyTitle: "هنوز داده‌ای ثبت نشده است",
  catalogEmptyBody:
    "هنوز داده‌ای برای این بخش ثبت نشده است. پس از تکمیل داده‌های اولیه توسط مدیر، رکوردها در همین صفحه نمایش داده می‌شوند.",
  catalogPreviewRestrictedTitle: "دسترسی پیش‌نمایش",
  catalogPreviewRestrictedBody:
    "حساب شما در حالت پیش‌نمایش است و برای مشاهده یا اجرای این بخش مجوز کافی ندارد. برای فعال‌سازی دسترسی، با اپراتور پروژه تماس بگیرید.",
  catalogPreviewRestrictedCta: "درخواست دسترسی",
  catalogPaginationLabel: "صفحه",
  catalogPrevPage: "قبلی",
  catalogNextPage: "بعدی",
  catalogReadOnlyNotice:
    "در این مرحله، کاتالوگ فقط برای مشاهده و کنترل اولیه فعال است. افزودن یا ویرایش رکوردها بعداً فعال می‌شود.",

  workflowDryRunTitle: "اجرای آزمایشی ممیزی بسته‌بندی",
  workflowDryRunSubtitle:
    "خروجی یک پیش‌نویس ساختاریافته فقط برای چشم بازبین است. موتور قطعی در این فاز هیچ ارائه‌دهنده هوش مصنوعی را فراخوانی نمی‌کند.",
  workflowDryRunFieldProductName: "نام محصول",
  workflowDryRunFieldProductCategory: "دسته محصول",
  workflowDryRunFieldMarket: "بازار هدف",
  workflowDryRunFieldChannel: "کانال فروش",
  workflowDryRunFieldPackageType: "نوع بسته",
  workflowDryRunFieldMaterial: "ماده ترجیحی",
  workflowDryRunFieldClaims: "ادعاها",
  workflowDryRunFieldClaimsHelp:
    "هر خط یک ادعا. حداکثر ۳۲ ادعا، هر کدام ۱۲۰ کاراکتر.",
  workflowDryRunFieldTargetCustomer: "مشتری هدف",
  workflowDryRunFieldLanguage: "زبان خروجی",
  workflowDryRunFieldNotes: "یادداشت‌ها",
  workflowDryRunSubmit: "اجرای آزمایشی ممیزی",
  workflowDryRunSubmitting: "در حال تولید پیش‌نویس…",
  workflowDryRunReset: "بازنشانی",
  workflowDryRunRequiredHint: "الزامی",
  workflowDryRunNotSelected: "— انتخاب نشده —",
  workflowDryRunValidationFailed:
    "لطفاً فیلدهای مشخص‌شده را بازبینی کنید و دوباره تلاش کنید.",

  aiAssistToggleLabel: "افزونه‌ی هوش مصنوعی",
  aiAssistToggleHint:
    "یک لایه‌ی مشورتی روی پیش‌نویس قطعی اضافه می‌کند. موتور قطعی همچنان مرجع اصلی است.",
  aiAssistDraftBanner:
    "پیش‌نویس با کمک هوش مصنوعی — همچنان نیازمند بازبینی انسانی است. هیچ تأیید مقرراتی و ضمانت تجاری ندارد.",
  aiAssistStatusOk: "لایه‌ی هوش مصنوعی الحاق شد",
  aiAssistStatusFallback:
    "دروازه‌ی هوش مصنوعی در دسترس نیست — پیش‌نویس قطعی نمایش داده می‌شود. بازبینی انسانی همچنان الزامی است.",
  aiAssistStatusOff: "فقط پیش‌نویس قطعی — هوش مصنوعی غیرفعال است.",
  aiAssistAuditHeader: "ممیزی هوش مصنوعی",

  reportInputSummary: "خلاصه ورودی",
  reportPackagingAudit: "ممیزی بسته‌بندی",
  reportPackagingAuditPositioning: "یادداشت‌های جایگاه‌یابی",
  reportPackagingAuditPackStructure: "یادداشت‌های ساختار بسته",
  reportPackagingAuditShelf: "ملاحظات قفسه و تحویل",
  reportLabelCopy: "پیشنهادهای برچسب / متن",
  reportLabelCopyFront: "روی بسته",
  reportLabelCopySide: "پنل کناری",
  reportLabelCopyTone: "یادداشت‌های لحن",
  reportMaterialNotes: "یادداشت‌های مواد",
  reportClaimRisk: "ریسک ادعا",
  reportClaimRiskLevelLabel: "سطح ریسک",
  reportClaimRiskFlags: "پرچم‌ها",
  reportClaimRiskReviewRequired: "نیازمند بازبینی",
  reportClaimRiskReviewReasons: "دلایل",
  reportSupplierBrief: "طرح بریف تأمین‌کننده",
  reportLaunchKit: "طرح کیت عرضه",
  reportTrustNotes: "یادداشت‌های اعتماد",
  reportNextSteps: "گام‌های بعدی",
  reportRiskLow: "پایین",
  reportRiskMedium: "متوسط",
  reportRiskHigh: "بالا",
  reportReviewRequiredYes: "بله",
  reportReviewRequiredNo: "خیر",
  reportHaltBannerHigh:
    "ادعاهای پرریسک تشخیص داده شد. تمام آماده‌سازی بسته‌بندی را تا تأیید یک بازبین ریسک ادعا و یک مشاور قانونی متوقف کنید. خروجی نگیرید.",
  reportHaltBannerMedium:
    "نگارش ادعای حساس تشخیص داده شد. یک بازبین واجد شرایط باید پیش از خروجی متن را تأیید کند.",
  reportNoFlags: "هیچ پرچمی وجود ندارد. در این سطح ریسک، تأیید بازبین لازم نیست.",

  intakeTitle: "ثبت بریف محصول و برند",
  intakeSubtitle:
    "بریف ساختاریافته‌ی محصول، برند، بازار، هدف، محدودیت‌ها و اسناد را ثبت کنید. موتور قطعی یک پیش‌نویس ساختاریافته فقط برای استفاده‌ی بازبین برمی‌گرداند — در این فاز هیچ ارائه‌دهنده‌ی AI فعال نیست.",
  intakeGroupProduct: "اطلاعات پایه‌ی محصول",
  intakeGroupBrand: "زمینه‌ی برند",
  intakeGroupMarket: "بازار و کانال",
  intakeGroupObjective: "هدف بسته‌بندی",
  intakeGroupConstraints: "محدودیت‌ها",
  intakeGroupAssets: "اسناد و دارایی‌ها",
  intakeFieldProductName: "نام محصول",
  intakeFieldProductCategory: "دسته‌ی محصول",
  intakeFieldProductSubcategory: "زیرشاخه",
  intakeFieldProductDescription: "توضیح محصول",
  intakeFieldProductForm: "فرم محصول",
  intakeFieldSizeValue: "مقدار اندازه",
  intakeFieldSizeUnit: "واحد اندازه",
  intakeFieldPriceBand: "بازه‌ی قیمت",
  intakeFieldBrandName: "نام برند",
  intakeFieldBrandPersonality: "شخصیت برند",
  intakeFieldBrandMaturity: "بلوغ برند",
  intakeFieldCurrentPackagingStatus: "وضعیت بسته‌بندی فعلی",
  intakeFieldVisualPreferences: "ترجیحات بصری",
  intakeFieldToneOfVoice: "لحن صدا",
  intakeFieldTargetMarkets: "بازارهای هدف",
  intakeFieldTargetMarketsHelp:
    "کدهای دو حرفی ISO با کاما جدا شوند (مثلاً AE، SA، TR). حداکثر ۸.",
  intakeFieldTargetLanguages: "زبان‌های هدف",
  intakeFieldSalesChannels: "کانال‌های فروش",
  intakeFieldTargetAudience: "مخاطب هدف",
  intakeFieldObjectives: "اهداف",
  intakeFieldObjectivesHelp: "حداقل یکی انتخاب کنید. چندتایی مجاز است.",
  intakeFieldMaterialConstraints: "محدودیت مواد",
  intakeFieldBudgetBand: "بازه‌ی بودجه",
  intakeFieldProductionConstraints: "محدودیت تولید",
  intakeFieldSustainabilityPreferences: "ترجیحات پایداری",
  intakeFieldSustainabilityPreferencesHelp:
    "تگ‌های کوتاه، هر خط یک تگ. حداکثر ۸.",
  intakeFieldClaimsToAvoid: "ادعاهایی که باید پرهیز شود",
  intakeFieldClaimsToAvoidHelp:
    "هر خط یک ادعا. حداکثر ۳۲، هر کدام ۱۲۰ کاراکتر.",
  intakeFieldAssetsAvailable: "اسناد در دسترس",
  intakeFieldLanguage: "زبان خروجی",
  intakeFieldNotes: "یادداشت‌ها",
  intakeSubmit: "اجرای ثبت بریف محصول",
  intakeSubmitting: "در حال تولید پیش‌نویس…",
  intakeReset: "بازنشانی",
  intakeReportProductBrief: "خلاصه‌ی بریف محصول",
  intakeReportBrandContext: "خلاصه‌ی زمینه‌ی برند",
  intakeReportObjectiveSummary: "خلاصه‌ی هدف بسته‌بندی",
  intakeReportMarketChannelFit: "یادداشت‌های تطابق بازار و کانال",
  intakeReportMissingChecklist: "چک‌لیست اطلاعات ناقص",
  intakeReportAssumptionLog: "سیاهه‌ی فرضیات",
  intakeReportInitialRiskFlags: "پرچم‌های ریسک اولیه",
  intakeReportHumanReview: "یادداشت‌های نیازمند بازبینی بشری",
  intakeReportRecommendedNextEngines: "موتورهای بعدی پیشنهادی",

  intakeReportStrategy: "اسنپ‌شات استراتژی بسته‌بندی",
  intakeReportTargetCustomer: "تفسیر مشتری هدف",
  intakeReportPositioning: "یادداشت‌های تموضع",
  intakeReportChannelReadiness: "یادداشت‌های آمادگی کانال",
  intakeReportLocalization: "یادداشت‌های زبان و بومی‌سازی",
  intakeReportDecisionPriorities: "اولویت‌های تصمیم بسته‌بندی",
  intakeReportOwnerQuestions: "پرسش‌های مالک برای حل",
  intakeNextLabelCopy: "بعدی: بازبینی برچسب و ادعا",

  labelCopyTitle: "بازبینی برچسب و ادعا",
  labelCopySubtitle:
    "اسکن قطعی ریسک ادعا + اسکلت متن برچسب. ادعاهای پرریسک هرگز به‌صورت «تأییدشده» بازنویسی نمی‌شوند. موتور هیچ استناد مقرراتی صادر نمی‌کند.",
  labelCopyFieldProductName: "نام محصول",
  labelCopyFieldCategory: "دسته محصول",
  labelCopyFieldBrandName: "نام برند",
  labelCopyFieldTargetMarket: "بازار هدف (ISO-2)",
  labelCopyFieldTargetLanguage: "زبان هدف",
  labelCopyFieldSalesChannel: "کانال فروش",
  labelCopyFieldProductDescription: "توضیح محصول",
  labelCopyFieldPackagingObjective: "هدف بسته‌بندی",
  labelCopyFieldClaimDrafts: "پیش‌نویس ادعا/متن",
  labelCopyFieldClaimDraftsHelp:
    "هر خط یک ادعا. حداکثر ۳۲، هر کدام ۲۰۰ کاراکتر.",
  labelCopyFieldToneOfVoice: "لحن صدا",
  labelCopyFieldClaimsToAvoid: "ادعاهای ممنوع",
  labelCopyFieldRequiredLabelElements: "عناصر الزامی برچسب",
  labelCopyFieldRequiredLabelElementsHelp:
    "هر خط یک عنصر. حداکثر ۱۲.",
  labelCopySubmit: "اجرای بازبینی برچسب و ادعا",
  labelCopySubmitting: "در حال تولید پیش‌نویس…",
  labelCopyReset: "بازنشانی",
  labelCopyReportFront: "پیش‌نویس روی بسته",
  labelCopyReportBack: "پیش‌نویس پشت بسته",
  labelCopyReportStory: "داستان کوتاه محصول",
  labelCopyReportInstructions: "دستورالعمل استفاده/سرو/نگهداری",
  labelCopyReportWarning: "هشدار / احتیاط",
  labelCopyReportClaimRiskTable: "جدول ریسک ادعا",
  labelCopyReportSaferWording: "پیشنهاد نگارش امن‌تر",
  labelCopyReportMissingInfo: "اطلاعات ناقص برچسب",
  labelCopyReportHumanReview: "نیازمند بازبینی بشری",
  labelCopyReportCopyQuality: "یادداشت‌های کیفیت متن",
  labelCopyClaimColClaim: "ادعا",
  labelCopyClaimColSeverity: "شدت",
  labelCopyClaimColCategory: "دسته",
  labelCopyClaimColMatched: "اصطلاحات منطبق",
  labelCopyClaimColReview: "بازبینی",

  materialTitle: "ماده و ساختار",
  materialSubtitle:
    "پیشنهاد قطعی فرمت بسته‌بندی / ماده / ساختار. بدون ضمانت مهندسی یا تولیدی. بازبینی بشری الزامی است.",
  materialFieldFragility: "شکنندگی",
  materialFieldTemperatureSensitive: "حساس به دما",
  materialFieldSustainabilityPreferences: "ترجیحات پایداری",
  materialReportFormat: "فرمت بسته‌بندی پیشنهادی",
  materialReportMaterial: "پیشنهاد ماده",
  materialReportStructure: "پیشنهاد ساختار",
  materialReportDurability: "دوام / مناولة",
  materialReportDelivery: "مناسب‌بودن برای تحویل",
  materialReportEcommerce: "مناسب‌بودن برای ecommerce",
  materialReportShelf: "مناسب‌بودن برای قفسه",
  materialReportProduction: "امکان‌سنجی تولید",
  materialReportCost: "حساسیت هزینه",
  materialReportSupplierQuestions: "پرسش‌های باز برای تأمین‌کننده",
  materialSubmit: "اجرای ماده و ساختار",
  materialSubmitting: "در حال تولید پیش‌نویس…",
  materialReset: "بازنشانی",
  intakeNextMaterial: "بعدی: ماده و ساختار",

  sustTitle: "ریسک پایداری و انطباق",
  sustSubtitle:
    "اسکن قطعی greenwashing / پایداری / انطباق. موتور هیچ ارجاع مقرراتی صادر نمی‌کند و هیچ ادعا را تأیید نمی‌کند.",
  sustFieldPackagingMaterial: "ماده‌ی بسته‌بندی",
  sustFieldClaims: "ادعاهای پایداری",
  sustFieldClaimsHelp: "هر خط یک ادعا. حداکثر ۳۲، هر کدام ۲۰۰ کاراکتر.",
  sustFieldLabelDrafts: "پیش‌نویس‌های برچسب/متن (اختیاری)",
  sustFieldRecyclable: "ادعای قابل بازیافت دارد",
  sustFieldBiodegradable: "ادعای biodegradable دارد",
  sustFieldCompostable: "ادعای compostable دارد",
  sustFieldOrigin: "ادعای origin / made-in دارد",
  sustFieldBarcode: "باربکد روی برچسب موجود است",
  sustFieldExpiry: "تاریخ انقضا / best-before موجود است",
  sustFieldOriginLabel: "برچسب کشور مبدأ موجود است",
  sustFieldWarning: "هشدارهای الزامی موجود است",
  sustReportClaimReview: "بازبینی ادعای پایداری",
  sustReportGreenwashing: "پرچم‌های ریسک greenwashing",
  sustReportSeverityTable: "جدول شدت ادعاها",
  sustReportSafer: "نگارش پایداری امن‌تر",
  sustReportCompliance: "چک‌لیست یادآور انطباق",
  sustReportCountry: "یادداشت‌های کشور / منطقه",
  sustReportEvidence: "شواهد لازم",
  sustReportNotApproved: "پیش‌نویس — تأیید نشده",
  sustSubmit: "اجرای ریسک پایداری و انطباق",
  sustSubmitting: "در حال تولید پیش‌نویس…",
  sustReset: "بازنشانی",
  sustColTerm: "اصطلاح",
  sustColSeverity: "شدت",
  sustColCategory: "دسته",
  sustColEvidence: "شواهد لازم",
  labelCopyNextSust: "بعدی: ریسک پایداری و انطباق",

  supplierTitle: "بریف تأمین‌کننده / RFQ",
  supplierSubtitle:
    "بریف تأمین‌کننده و پیش‌نویس RFQ قطعی. موتور هرگز ارسال نمی‌کند، تأمین‌کننده‌ای انتخاب نمی‌کند، و قیمت نهایی یا تأیید تولید ادعا نمی‌کند.",
  supplierFieldFormat: "فرمت بسته‌بندی",
  supplierFieldMaterial: "ماده‌ی بسته‌بندی",
  supplierFieldStructure: "پیشنهاد ساختار",
  supplierFieldQuantity: "بازه‌ی مقدار",
  supplierFieldPrintFinish: "چاپ / فینیش",
  supplierFieldKnownConstraints: "محدودیت‌های شناخته‌شده",
  supplierFieldQuestions: "پرسش‌ها برای تأمین‌کننده",
  supplierFieldQuestionsHelp: "هر خط یک پرسش. حداکثر ۱۲.",
  supplierReportBrief: "خلاصه‌ی بریف تأمین‌کننده",
  supplierReportSpec: "پیش‌نویس مشخصات بسته‌بندی",
  supplierReportMaterial: "نیازمندی‌های ماده / ساختار",
  supplierReportPrint: "نیازمندی‌های چاپ / فینیش",
  supplierReportQuantity: "پرسش‌های مقدار / MOQ",
  supplierReportLead: "پرسش‌های lead time",
  supplierReportQuality: "پرسش‌های کیفیت / آزمون",
  supplierReportSust: "پرسش‌های پایداری / شواهد",
  supplierReportRfqEmail: "پیش‌نویس ایمیل RFQ (موتور ارسال نمی‌کند)",
  supplierReportCompare: "معیارهای مقایسه‌ی تأمین‌کنندگان",
  supplierReportMissing: "اطلاعات تولیدی ناقص",
  supplierSubmit: "اجرای بریف تأمین‌کننده / RFQ",
  supplierSubmitting: "در حال تولید پیش‌نویس…",
  supplierReset: "بازنشانی",
  materialNextSupplier: "بعدی: بریف تأمین‌کننده / RFQ",
  sustNextSupplier: "بعدی: بریف تأمین‌کننده / RFQ (درخواست شواهد)",

  visualTitle: "جهت‌گیری بصری",
  visualSubtitle:
    "بریف جهت‌گیری بصری قطعی. موتور هرگز تصویر تولید نمی‌کند، فنّ رقیب را کپی نمی‌کند، و artwork نهایی را تأیید نمی‌کند.",
  visualFieldBrandPersonality: "شخصیت برند",
  visualFieldTargetCustomer: "مشتری هدف",
  visualFieldVisualPreference: "ترجیح بصری",
  visualFieldColorsToUse: "رنگ‌های مورد استفاده",
  visualFieldColorsToAvoid: "رنگ‌های مورد اجتناب",
  visualFieldTypographyPreference: "ترجیح تایپوگرافی",
  visualFieldCompetitorReferences: "ارجاعات رقبا",
  visualFieldCompetitorReferencesHelp:
    "هر خط یک ارجاع. صرفاً برای الهام — هرگز کپی نشود.",
  visualReportSummary: "خلاصه‌ی جهت‌گیری بصری",
  visualReportRoutes: "گزینه‌های مسیر طراحی",
  visualReportColor: "جهت‌گیری رنگ",
  visualReportTypography: "جهت‌گیری تایپوگرافی",
  visualReportLayout: "جهت‌گیری چیدمان",
  visualReportSurface: "اولویت سطوح بسته‌بندی",
  visualReportShelfEc: "یادداشت‌های بصری قفسه / ecommerce",
  visualReportCultural: "یادداشت‌های فرهنگی / بومی‌سازی",
  visualReportMockup: "بسته‌ی پرامپت موکاپ",
  visualReportHandoff: "یادداشت‌های تحویل به طراح",
  visualReportRisks: "ریسک‌های بصری / اجتناب",
  visualSubmit: "اجرای جهت‌گیری بصری",
  visualSubmitting: "در حال تولید پیش‌نویس…",
  visualReset: "بازنشانی",
  intakeNextVisual: "بعدی: جهت‌گیری بصری",
  labelCopyNextVisual: "بعدی: جهت‌گیری بصری",

  scorecardTitle: "کارنامه‌ی بسته‌بندی و بسته‌ی عمودی",
  scorecardSubtitle:
    "امتیازدهی قطعی ۰–۱۰۰ برای قفسه / ecommerce / تحویل / unboxing / marketplace با اعمال bias بسته‌ی عمودی منتخب. بدون تحلیل تصویر. بازبینی بشری الزامی.",
  scorecardFieldVerticalPack: "بسته‌ی عمودی",
  scorecardFieldShelfEnv: "محیط قفسه",
  scorecardFieldFragility: "شکنندگی",
  scorecardFieldLiquid: "مایع",
  scorecardFieldTempSensitive: "حساس به دما",
  scorecardFieldThumbnailOpt: "thumbnail ecommerce بهینه شده",
  scorecardFieldProtectiveInsert: "حشوه‌ی حفاظتی دارد",
  scorecardFieldLeakSeal: "ختم ضد نشتی دارد",
  scorecardFieldDropTest: "آزمون سقوط را گذرانده",
  scorecardFieldVisualNotes: "یادداشت‌های جهت‌گیری بصری",
  scorecardOverall: "امتیاز کلی بسته‌بندی",
  scorecardSectionShelf: "دیده‌شدن در قفسه",
  scorecardSectionEcommerce: "آمادگی thumbnail",
  scorecardSectionDelivery: "دوام تحویل",
  scorecardSectionUnboxing: "تجربه‌ی unboxing",
  scorecardSectionMarketplace: "آمادگی marketplace",
  scorecardVerticalPackLabel: "بسته‌ی عمودی",
  scorecardRiskFlags: "پرچم‌های ریسک",
  scorecardImprovements: "پیشنهادهای بهبود",
  scorecardChannelRecs: "توصیه‌ها بر اساس کانال",
  scorecardMissing: "اطلاعات ناقص",
  scorecardSubmit: "اجرای کارنامه‌ی بسته‌بندی",
  scorecardSubmitting: "در حال تولید پیش‌نویس…",
  scorecardReset: "بازنشانی",
  scorecardBandHigh: "بالا",
  scorecardBandMedium: "متوسط",
  scorecardBandLow: "پایین",
  visualNextScorecard: "بعدی: کارنامه‌ی بسته‌بندی",
  supplierNextScorecard: "بعدی: کارنامه‌ی بسته‌بندی",
  launchKitTitle: "حقیبه‌ی راه‌اندازی",
  launchKitSubtitle: "پیش‌نویس‌های متن قطعی برای بازبینی بشری. موتور هرگز post نمی‌کند و هرگز approval نهایی ادعا نمی‌کند.",
  launchKitFieldPositioning: "تموضع packaging",
  launchKitFieldKeyBenefit: "فایده‌ی کلیدی",
  launchKitFieldClaimRestrictions: "ادعاهای محدودشده",
  launchKitFieldQrConnected: "QR / connected packaging",
  launchKitFieldLaunchChannels: "کانال‌های راه‌اندازی",
  launchKitReportPositioning: "خلاصه‌ی تموضع راه‌اندازی",
  launchKitReportListing: "پیش‌نویس listing ecommerce",
  launchKitReportSocial: "پیش‌نویس‌های محتوای social",
  launchKitReportInsert: "متن کارت insert",
  launchKitReportQr: "ایده‌ی QR / متصل",
  launchKitReportLoyalty: "متن بازبینی / reorder / loyalty",
  launchKitReportSafety: "یادداشت‌های ایمنی ادعا",
  launchKitSubmit: "اجرای حقیبه‌ی راه‌اندازی",
  launchKitSubmitting: "در حال تولید پیش‌نویس…",
  launchKitReset: "بازنشانی",
  postLaunchTitle: "تحلیل پس از راه‌اندازی",
  postLaunchSubtitle: "فقط متن paste-شده توسط اپراتور. بدون scraping خارجی، بدون marketplace API، بدون PII، بدون ادعای statistical.",
  postLaunchFieldReviews: "snippetهای review (هر خط یک)",
  postLaunchFieldReturns: "دلایل return (هر خط یک)",
  postLaunchFieldFeedback: "یادداشت feedback مشتری (هر خط یک)",
  postLaunchFieldPerformance: "یادداشت‌های عملکرد ecommerce",
  postLaunchFieldVersion: "نسخه‌ی packaging",
  postLaunchReportThemes: "خلاصه‌ی محور feedback",
  postLaunchReportComplaints: "طبقه‌بندی شکایت‌های packaging",
  postLaunchReportReturns: "طبقه‌بندی return / damage",
  postLaunchReportSentiment: "یادداشت‌های sentiment review",
  postLaunchReportImprovements: "پیشنهاد بهبود packaging",
  postLaunchReportNext: "اولویت‌های نسخه‌ی بعدی",
  postLaunchReportDataQuality: "کیفیت داده / داده‌ی ناقص",
  postLaunchSubmit: "اجرای تحلیل پس از راه‌اندازی",
  postLaunchSubmitting: "در حال تولید پیش‌نویس…",
  postLaunchReset: "بازنشانی",
  reviewChecklistTitle: "checklist بازبینی",
  reviewChecklistSubtitle: "checklist قطعی + قالب بازبینی ریسک + آمادگی export. بدون DB، بدون persistence، بدون approval نهایی.",
  reviewChecklistFieldTarget: "هدف workflow",
  reviewChecklistFieldHighRisk: "ادعای high-risk حاضر",
  reviewChecklistFieldEvidenceGap: "فجوه‌ی شواهد حاضر",
  reviewChecklistFieldSustClaim: "ادعای پایداری بدون پشتوانه",
  reviewChecklistFieldFoodContact: "نگرانی food-contact",
  reviewChecklistFieldCosmetic: "نگرانی ایمنی cosmetic",
  reviewChecklistFieldIndustrial: "نگرانی hazard صنعتی",
  reviewChecklistFieldSupplier: "عدم قطعیت تأمین‌کننده",
  reviewChecklistReportChecklist: "checklist بازبینی",
  reviewChecklistReportCriteria: "معیارهای تأیید",
  reviewChecklistReportRisks: "آیتم‌های بازبینی ریسک",
  reviewChecklistReportEvidence: "شواهد لازم",
  reviewChecklistReportExport: "آمادگی export",
  reviewChecklistReportDecision: "قالب تصمیم بازبین",
  reviewChecklistSubmit: "اجرای checklist بازبینی",
  reviewChecklistSubmitting: "در حال تولید پیش‌نویس…",
  reviewChecklistReset: "بازنشانی",
  servicesTitle: "خدمات و پک‌های هوش بسته‌بندی",
  servicesIntro:
    "Packaging OS به تیم‌ها کمک می‌کند پیش از هزینه‌کردن برای طراحی، چاپ، تأمین‌کننده، ادعاها یا لانچ، تصمیم‌های بسته‌بندی بهتری بگیرند. هر خروجی یک پیش‌نویس پشتیبان تصمیم است.",
  servicesHumanReview: "بازبینی انسانی برای هر خروجی الزامی است",
  servicesNotLegal: "تأیید حقوقی یا مقرراتی نیست",
  servicesNoBilling: "پرداخت آنلاین وجود ندارد — همکاری با ثبت درخواست آغاز می‌شود",
  packPilotReady: "درخواست پایلوت باز است",
  packFoundation: "زیرساخت تجاری",
  packKnownGaps: "شکاف‌های شناخته‌شده",
  packWorkflows: "جریان‌های کاری شامل",
  packRequestQuote: "درخواست قیمت",
  pricingTitle: "بسته‌ها و قیمت‌گذاری",
  pricingIntro:
    "قیمت‌ها برای هر همکاری جداگانه نهایی می‌شود. پرداخت یا اشتراک آنلاین وجود ندارد — درخواست بدهید تا همکاران ما تماس بگیرند.",
  pricingDraftLabel: "قیمت‌گذاری پیش‌نویس — درخواست قیمت بدهید",
  requestTitle: "درخواست دسترسی، دمو، ممیزی یا پایلوت",
  requestIntro:
    "دربارهٔ محصول و بازار خود بگویید. درخواست‌ها ذخیره و توسط انسان بررسی می‌شوند — ایمیل خودکار ارسال نمی‌شود.",
  requestTypeLabel: "چه چیزی نیاز دارید؟",
  requestTypeAccess: "دسترسی به پلتفرم",
  requestTypeDemo: "دمو",
  requestTypeAudit: "ممیزی بسته‌بندی",
  requestTypePilot: "پروژه پایلوت",
  requestNameLabel: "نام شما",
  requestEmailLabel: "ایمیل",
  requestCompanyLabel: "شرکت / برند",
  requestCountryLabel: "کشور / بازار",
  requestLanguageLabel: "زبان ترجیحی",
  requestMessageLabel: "پیام (اختیاری)",
  requestSubmit: "ثبت درخواست",
  requestSubmitting: "در حال ارسال…",
  requestSuccess: "درخواست شما ذخیره شد. تیم آن را بررسی و با شما تماس می‌گیرد.",
  requestNoEmailNote: "در این مرحله ایمیل خودکار ارسال نمی‌شود.",
  requestValidationError: "لطفاً نام و ایمیل معتبر وارد کنید.",
  adminTitle: "کنسول مدیریت",
  adminOverviewTab: "نمای کلی",
  adminReviewOpsTab: "عملیات بازبینی",
  adminCommercialTab: "تجاری",
  adminSupportTab: "پشتیبانی",
  adminBlockedMessage: "این کنسول به مجوز مالک/ادمین نیاز دارد.",
  adminCardUsers: "کاربران",
  adminCardRuns: "اجرای جریان‌های کاری",
  adminCardReview: "مستندات در انتظار بازبینی",
  adminCardGaps: "شکاف‌های منبع باز",
  adminCardKnowledge: "فایل‌های دانش",
  adminCardCost: "هزینه کل (دلار)",
  adminCardRequests: "درخواست‌های تجاری جدید",
  adminAiModeLabel: "حالت هوش مصنوعی",
  adminBillingDisabled: "صورتحساب غیرفعال — درگاه پرداختی وجود ندارد",
  runSavedBanner: "اجرا ذخیره شد — مستند پیش‌نویس ساخته شد (نیازمند بازبینی انسانی)",
  runSavedArtifactLink: "بازکردن مستند",
  navArtifacts: "مستندات و خروجی‌ها",
  navKnowledge: "دانش",
  navAdmin: "کنسول مدیریت",
  artifactsTitle: "مستندات و خروجی‌ها",
  artifactsIntro: "هر اجرای ذخیره‌شده یک مستند پیش‌نویس می‌سازد. اینجا بازبینی، تأیید و خروجی بگیرید — خروجی نهایی تا تأیید، مسدود می‌ماند.",
  artifactStatusLabel: "وضعیت",
  artifactReviewApprove: "تأیید",
  artifactReviewReject: "رد",
  artifactExportDraft: "خروجی پیش‌نویس (MD)",
  artifactExportFinal: "خروجی نهایی (PDF)",
  artifactExportFinalGated: "خروجی نهایی به مستند تأییدشده نیاز دارد.",
  artifactDownload: "دانلود",
  artifactSourceGaps: "شکاف‌های منبع باز",
  artifactEvidence: "شواهد پیوندشده",
  artifactVersions: "نسخه‌ها",
  knowledgeTitle: "مغز دانش",
  knowledgeIntro: "اسناد مرجع را بارگذاری کنید (PDF/DOCX/TXT/CSV/XLSX). محتوای بارگذاری‌شده حقیقت نیست — هشدارهای استخراج قابل‌مشاهده می‌مانند و شواهد به تأیید انسانی نیاز دارند.",
  knowledgeUploadLabel: "فایل سند",
  knowledgeLanguageHint: "زبان منبع (اعلام‌شده)",
  knowledgeUploadButton: "بارگذاری",
  knowledgeExtractButton: "استخراج",
  knowledgeSearchLabel: "جستجو در دانش فضای کاری",
  knowledgeSearchButton: "جستجو",
  knowledgeWarnings: "هشدارهای استخراج",
  knowledgeNoOcrNote: "PDF اسکن‌شده پشتیبانی نمی‌شود (بدون OCR) — صادقانه با هشدار شکست می‌خورد.",
};

const tr: UiStrings = {
  appName: "Packaging OS",
  switchLanguageLabel: "Dili değiştir",
  copyrightLine: "© 2026 Packaging OS",

  trustBannerFull:
    "Bu bir taslak yüzeydir. Nihai veya ticari bir çıktı değildir. Düzenleyici garanti yoktur. Yapay zeka sağlayıcıları bu aşamada etkin değildir.",
  trustBannerShort: "Taslak yüzey. Düzenleyici garanti yoktur.",
  trustNotesSourceBacked: "Kaynak destekli: Hayır",
  trustNotesRegulatoryGuarantee: "Düzenleyici garanti: Hayır",
  trustNotesFinalOutput: "Nihai çıktı: Hayır",

  ctaSignInWithGoogle: "Google ile devam et",
  ctaRequestAccess: "Erişim talep et",
  ctaOpenDashboard: "Çalışma alanını aç",
  ctaSignOut: "Oturumu kapat",
  ctaBackToLanding: "Ana sayfaya dön",

  landingHeroTitle:
    "Ambalaj Zekâsı ve Ürün Deneyimi İşletim Sistemi",
  landingHeroSubtitle:
    "Ambalaj denetimi, etiket ve metin incelemesi, malzeme notları, iddia riski ve tedarikçi brifi taslakları için kontrollü, profesyonel bir çalışma alanı. Operatör odaklı. Önce güven.",
  landingTrustStripTitle: "Ürünün şu anki durumu",
  landingTrustStripDraftItem:
    "Yalnızca taslak — bu yüzeyden henüz hiçbir nihai veya ticari çıktı sevk edilmiyor.",
  landingTrustStripNoLiveAiItem:
    "Yapay zeka sağlayıcıları bu aşamada etkin değildir. Motor çıktıları belirleyici ve kural tabanlıdır.",
  landingTrustStripControlledAccessItem:
    "Erişim bir yönetici tarafından kontrol edilir. Self-servis kayıt yoktur.",
  landingCapabilityHeading: "Kapının ardında ne var",
  landingCapabilityCatalogTitle: "Katalog",
  landingCapabilityCatalogBody:
    "Markalar, ürünler ve ambalaj projeleri inceleme için sürüm ve aktif kayıt disiplini ile düzenlenmiştir.",
  landingCapabilityWorkflowsTitle: "İş Akışları",
  landingCapabilityWorkflowsBody:
    "Dry-run ambalaj denetimi, iddia risk işaretleri ve tedarikçi brif taslağı ile incelemeciler için yapılandırılmış bir taslak üretir.",
  landingCapabilityTrustTitle: "Güven ve Güvenlik",
  landingCapabilityTrustBody:
    "Her çıktı açık bir durum taşır: taslak / kaynak / düzenleyici / inceleme-gerekli. Dışa aktarımdan önce inceleme onayı zorunludur.",
  landingWorkflowPreviewHeading: "Denetim akışı nasıl çalışır",
  landingWorkflowPreviewBody:
    "Operatörler bir ambalaj brifi gönderir — ürün, pazar, kanal, iddialar. Belirleyici motor; bir taslak denetim, etiket ve metin notları, malzeme notları, iddia riski, tedarikçi brif taslağı ve lansman kiti taslağı döndürür. Bir incelemeci onaylamadan hiçbir şey nihai değildir.",
  landingAccessHeading: "Erişim modeli",
  landingAccessBody:
    "Roller, platform kimlik geçidinde bir yönetici tarafından atanır. Önizleme hesapları yalnızca kataloğa göz atabilir; editörler ve marka sahipleri dry-run denetimi çalıştırabilir; incelemeciler onaylar. Self-servis terfi yoktur.",
  landingCtaSectionHeading: "Girmeye hazır mısınız?",
  landingCtaSectionBody:
    "Atanmış bir rolünüz varsa oturum açın. Aksi halde erişim talep edin — her talep manuel olarak bir yönetici tarafından incelenir.",
  landingFooterEcosystem:
    "Packaging OS, ai-platform-core ekosistemi üzerinde dikey bir uygulamadır. Kimlik, denetim ve yapay zeka yürütmesi Core'a devredilir.",

  loginTitle: "Packaging OS'a giriş",
  loginGoogleHelperText:
    "Oturum açma Core kimlik geçidi tarafından yönetilir. Packaging OS şifrenizi asla görmez.",
  loginEmailComingSoonLabel: "E-posta ve şifre — yakında",
  loginEmailComingSoonTooltip:
    "E-posta ve şifre ile oturum açma bu aşamada etkin değildir. ai-platform-core üzerinde önceden hazırlanmış bir hesap gereklidir.",
  loginRequestAccessLead: "Henüz erişiminiz yok mu?",
  loginTrustParagraph:
    "Rolünüz bir yönetici tarafından atanır. Oturum açma kimliği doğrular ancak ek erişim sağlamaz.",
  loginCoreUrlMissingNotice:
    "Core oturum açma URL'si bu yapıda yapılandırılmadı. Operatörün, oturum açma erişilebilir olmadan önce VITE_CORE_AUTH_START_URL değerini ayarlaması gerekir.",
  loginEmailLabel: "E-posta",
  loginPasswordLabel: "Şifre",
  loginSubmitLabel: "Giriş yap",
  loginSubmittingLabel: "Giriş yapılıyor…",
  loginErrorInvalidCredentials: "E-posta veya şifre hatalı.",
  loginErrorSuspended:
    "Bu hesap askıya alınmıştır. Lütfen platform yöneticisiyle iletişime geçin.",
  loginErrorUnavailable:
    "Oturum açma geçici olarak kullanılamıyor. Lütfen tekrar deneyin.",
  loginGoogleUnavailableNotice:
    "Google ile giriş henüz kullanılamıyor. E-posta ve şifre kullanın.",
  loginPreviewModeNotice:
    "Hesabınız Önizleme Modundadır. Tam erişimi etkinleştirmek için lütfen platform yöneticisiyle iletişime geçin.",
  loginPreviewContinue: "Önizleme Modunda devam et",
  loginErrorOauthCancelled:
    "Oturum açma iptal edildi. Tekrar deneyebilir veya erişim talep edebilirsiniz.",
  loginErrorOauthFailed:
    "Oturum açma tamamlanamadı. Lütfen tekrar deneyin. Sorun devam ederse yöneticinizle iletişime geçin.",
  loginErrorServiceDown:
    "Oturum açma hizmetine şu anda ulaşamıyoruz. Kısa bir süre sonra tekrar deneyin.",
  loginPostauthChecking: "Oturum açma tamamlanıyor…",
  loginPostauthSessionMissing:
    "Oturum açma geri döndü, ancak Packaging oturumu henüz kullanılabilir değil. Tekrar deneyin veya operatöre başvurun.",
  loginPostauthAccessDenied:
    "Oturum açma başarılı, ancak hesabınızın bu çalışma alanına erişimi yok. Operatöre başvurun.",
  loginPostauthUnavailable:
    "Oturum doğrulanamadı. Kısa bir süre sonra tekrar deneyin.",
  loginPostauthContinue: "Çalışma alanına devam et",

  previewTitle: "Önizleme modundasınız.",
  previewBanner:
    "Katalog salt okunurdur ve denetim iş akışı yönetici tarafından atanan bir rolün arkasındadır.",
  previewYouCanHeading: "Yapabilecekleriniz",
  previewYouCanItem1: "Erişim özetinizi ve rollerinizi görüntüleyin.",
  previewYouCanItem2: "Genel sayfayı ve güven duruşunu okuyun.",
  previewLockedHeading: "Kilitli olanlar",
  previewLockedAuditReason:
    "Ambalaj denetimi çalıştır — packaging.workflow.start izni gerektirir.",
  previewLockedExportReason:
    "Sonuçları dışa aktar — Faz 2, gerçek iş akışı yürütmesi bekleniyor.",
  previewLockedReviewReason:
    "İncelemeciye gönder — incelemeci rolü gerektirir.",
  previewLockedSupplierReason:
    "Tam tedarikçi brifi oluştur — Faz 2, gerçek iş akışı yürütmesi bekleniyor.",
  previewContactAdminLead: "Sorun mu var?",
  previewRequestAccessNotice:
    "Erişim, inceleme sonrası bir yönetici tarafından manuel olarak verilir. Bu aşamada otomatik yükseltme veya ödeme yoktur.",

  appShellTitle: "Kimliği doğrulanmış çalışma alanı kabuğu",
  appShellSubtitle:
    "Çalışma alanı kabuğu hazırdır. Ayrıntılı katalog ve iş akışı ekranları sonraki aşamada açılır.",
  appShellWorkspacePending:
    "Katalog ve dry-run iş akışı yüzeyleri burada henüz oluşturulmadı. Korunan arka uç bunları zaten sunuyor.",
  appShellApiStatusHeading: "API durumu",
  appShellNavCatalog: "Katalog",
  appShellNavWorkflows: "İş akışları",
  appShellNavTrust: "Güven ve Güvenlik",
  appShellNavSettings: "Ayarlar",
  appShellSectionPlaceholder:
    "Bu bölüm sonraki aşamada açılır.",
  appShellComingSoonBrands: "Markalar listesi — 1O-B aşamasında açılır.",
  appShellComingSoonProducts: "Ürünler listesi — 1O-B aşamasında açılır.",
  appShellComingSoonProjects: "Ambalaj projeleri listesi — 1O-B aşamasında açılır.",
  appShellComingSoonAudit: "Dry-run ambalaj denetimi — 1O-B aşamasında açılır.",

  whoAmIChecking: "Oturumunuz kontrol ediliyor…",
  whoAmIOkLead: "Oturum açan kullanıcı",
  whoAmIRoles: "Roller",
  whoAmIPermissionsCount: "İzinler",
  whoAmIAccessModeLabel: "Erişim modu",
  whoAmIPreviewLabel: "Önizleme modu",
  accessModeSuperAdmin: "Süper yönetici",
  accessModeAdmin: "Yönetici",
  accessModeOperator: "Operatör",
  accessModeUser: "Kullanıcı",
  accessModePreview: "Önizleme erişimi",
  accessModeUnknown: "Bilinmiyor",

  errAuthMissing: "Operatör çalışma alanına erişmek için oturum açın.",
  errAuthInvalid: "Oturumunuzun süresi doldu. Lütfen tekrar oturum açın.",
  errPermissionDenied:
    "Rolünüz bu işlem için gereken izni içermiyor.",
  errRateLimited:
    "Çok fazla istek. Lütfen biraz bekleyip tekrar deneyin.",
  errServiceUnavailable:
    "Packaging OS hizmeti geçici olarak düşük performansla çalışıyor. Kısa bir süre sonra tekrar deneyin.",
  errNetwork:
    "Packaging OS API'sine ulaşamadık. Bağlantınızı kontrol edip tekrar deneyin.",
  errUnknown:
    "Beklenmeyen bir hata oluştu. Tekrar deneyin ve sorun devam ederse istek kimliğini destek ekibiyle paylaşın.",
  errRequestIdLabel: "İstek kimliği:",

  notFoundTitle: "Sayfa bulunamadı",
  notFoundBody:
    "Aradığınız sayfayı bulamadık. Bilinen bir bölüme dönmek için gezinmeyi kullanın.",

  catalogBrandsTitle: "Markalar",
  catalogProductsTitle: "Ürünler",
  catalogProjectsTitle: "Ambalaj projeleri",
  catalogColumnName: "Ad",
  catalogColumnSlug: "Kısa kod",
  catalogColumnVersion: "Sürüm",
  catalogColumnUpdated: "Güncellendi",
  catalogColumnCategory: "Kategori",
  catalogColumnStatus: "Durum",
  catalogColumnMarkets: "Pazarlar",
  catalogColumnLanguages: "Diller",
  catalogEmptyTitle: "Henüz kayıt yok",
  catalogEmptyBody:
    "Bu bölüm için henüz veri kaydedilmedi. Yönetici ilk kurulumu tamamladığında kayıtlar burada görünür.",
  catalogPreviewRestrictedTitle: "Yalnızca önizleme erişimi",
  catalogPreviewRestrictedBody:
    "Hesabınız önizleme modundadır ve bu bölümü görüntüleme veya çalıştırma izniniz yok. Erişimi etkinleştirmek için proje operatörüne başvurun.",
  catalogPreviewRestrictedCta: "Erişim talep et",
  catalogPaginationLabel: "Sayfa",
  catalogPrevPage: "Önceki",
  catalogNextPage: "Sonraki",
  catalogReadOnlyNotice:
    "Bu aşamada katalog yalnızca görüntüleme ve ilk inceleme için kullanılabilir. Kayıt ekleme ve düzenleme daha sonra etkinleştirilecektir.",

  workflowDryRunTitle: "Ambalaj denetimi deneme çalıştırması",
  workflowDryRunSubtitle:
    "Çıktı yalnızca incelemeci için yapılandırılmış bir TASLAK'tır. Belirleyici motor bu aşamada hiçbir yapay zeka sağlayıcısını çağırmaz.",
  workflowDryRunFieldProductName: "Ürün adı",
  workflowDryRunFieldProductCategory: "Ürün kategorisi",
  workflowDryRunFieldMarket: "Hedef pazar",
  workflowDryRunFieldChannel: "Satış kanalı",
  workflowDryRunFieldPackageType: "Ambalaj türü",
  workflowDryRunFieldMaterial: "Tercih edilen malzeme",
  workflowDryRunFieldClaims: "İddialar",
  workflowDryRunFieldClaimsHelp:
    "Her satıra bir iddia. En fazla 32 iddia, her biri 120 karakter.",
  workflowDryRunFieldTargetCustomer: "Hedef müşteri",
  workflowDryRunFieldLanguage: "Çıktı dili",
  workflowDryRunFieldNotes: "Notlar",
  workflowDryRunSubmit: "Dry-run denetimi çalıştır",
  workflowDryRunSubmitting: "Taslak oluşturuluyor…",
  workflowDryRunReset: "Sıfırla",
  workflowDryRunRequiredHint: "Zorunlu",
  workflowDryRunNotSelected: "— seçilmedi —",
  workflowDryRunValidationFailed:
    "Lütfen vurgulanan alanları gözden geçirin ve tekrar deneyin.",

  aiAssistToggleLabel: "Yapay zeka destekli katman",
  aiAssistToggleHint:
    "Belirleyici taslağın üstüne tavsiye niteliğinde bir katman ekler. Belirleyici motor önce çalışır ve yetkilidir.",
  aiAssistDraftBanner:
    "Yapay zeka destekli taslak — yine de insan incelemesi gerektirir. Düzenleyici onay yok, ticari garanti yok.",
  aiAssistStatusOk: "Yapay zeka katmanı eklendi",
  aiAssistStatusFallback:
    "Yapay zeka geçidi kullanılamıyor — belirleyici taslak gösteriliyor. İncelemeci onayı hâlâ gereklidir.",
  aiAssistStatusOff: "Yalnızca belirleyici taslak — yapay zeka devre dışı.",
  aiAssistAuditHeader: "Yapay zeka denetim kaydı",

  reportInputSummary: "Girdi özeti",
  reportPackagingAudit: "Ambalaj denetimi",
  reportPackagingAuditPositioning: "Konumlandırma notları",
  reportPackagingAuditPackStructure: "Ambalaj yapısı notları",
  reportPackagingAuditShelf: "Raf ve teslimat değerlendirmeleri",
  reportLabelCopy: "Etiket / metin önerileri",
  reportLabelCopyFront: "Ön yüz",
  reportLabelCopySide: "Yan panel",
  reportLabelCopyTone: "Ton notları",
  reportMaterialNotes: "Malzeme notları",
  reportClaimRisk: "İddia riski",
  reportClaimRiskLevelLabel: "Risk seviyesi",
  reportClaimRiskFlags: "İşaretler",
  reportClaimRiskReviewRequired: "İnceleme gerekli",
  reportClaimRiskReviewReasons: "Nedenler",
  reportSupplierBrief: "Tedarikçi brif taslağı",
  reportLaunchKit: "Lansman kiti taslağı",
  reportTrustNotes: "Güven notları",
  reportNextSteps: "Sonraki adımlar",
  reportRiskLow: "düşük",
  reportRiskMedium: "orta",
  reportRiskHigh: "yüksek",
  reportReviewRequiredYes: "Evet",
  reportReviewRequiredNo: "Hayır",
  reportHaltBannerHigh:
    "Yüksek riskli iddialar tespit edildi. Bir iddia-risk incelemecisi VE bir mevzuat danışmanı onaylayana kadar tüm ambalaj prova çalışmalarını durdurun. Dışa aktarmayın.",
  reportHaltBannerMedium:
    "Hassas iddia ifadesi tespit edildi. Dışa aktarımdan önce metni nitelikli bir incelemecinin onaylaması gerekir.",
  reportNoFlags: "İşaret yok. Bu risk seviyesinde incelemeci onayı gerekmez.",

  intakeTitle: "Ürün ve Marka Brief Alımı",
  intakeSubtitle:
    "Ürün, marka, pazar, hedef, kısıt ve varlık verilerini yapılandırılmış bir brief olarak yakalayın. Belirleyici motor yalnızca incelemeci kullanımı için bir TASLAK üretir — bu aşamada hiçbir yapay zeka sağlayıcı çalıştırılmaz.",
  intakeGroupProduct: "Ürün temelleri",
  intakeGroupBrand: "Marka bağlamı",
  intakeGroupMarket: "Pazar ve kanal",
  intakeGroupObjective: "Ambalaj hedefi",
  intakeGroupConstraints: "Kısıtlar",
  intakeGroupAssets: "Mevcut varlıklar",
  intakeFieldProductName: "Ürün adı",
  intakeFieldProductCategory: "Ürün kategorisi",
  intakeFieldProductSubcategory: "Alt kategori",
  intakeFieldProductDescription: "Ürün açıklaması",
  intakeFieldProductForm: "Ürün formu",
  intakeFieldSizeValue: "Boyut değeri",
  intakeFieldSizeUnit: "Boyut birimi",
  intakeFieldPriceBand: "Fiyat bandı",
  intakeFieldBrandName: "Marka adı",
  intakeFieldBrandPersonality: "Marka kişiliği",
  intakeFieldBrandMaturity: "Marka olgunluğu",
  intakeFieldCurrentPackagingStatus: "Mevcut ambalaj",
  intakeFieldVisualPreferences: "Görsel tercihler",
  intakeFieldToneOfVoice: "Ses tonu",
  intakeFieldTargetMarkets: "Hedef pazarlar",
  intakeFieldTargetMarketsHelp:
    "Virgülle ayrılmış ISO-2 ülke kodları (örn. AE, SA, TR). En fazla 8.",
  intakeFieldTargetLanguages: "Hedef diller",
  intakeFieldSalesChannels: "Satış kanalları",
  intakeFieldTargetAudience: "Hedef kitle",
  intakeFieldObjectives: "Hedefler",
  intakeFieldObjectivesHelp:
    "En az birini seçin. Birden fazla seçilebilir.",
  intakeFieldMaterialConstraints: "Malzeme kısıtları",
  intakeFieldBudgetBand: "Bütçe bandı",
  intakeFieldProductionConstraints: "Üretim kısıtları",
  intakeFieldSustainabilityPreferences: "Sürdürülebilirlik tercihleri",
  intakeFieldSustainabilityPreferencesHelp:
    "Kısa etiketler, satır başına bir tane. En fazla 8.",
  intakeFieldClaimsToAvoid: "Kaçınılacak iddialar",
  intakeFieldClaimsToAvoidHelp:
    "Satır başına bir iddia. En fazla 32, her biri 120 karakter.",
  intakeFieldAssetsAvailable: "Mevcut varlıklar",
  intakeFieldLanguage: "Çıktı dili",
  intakeFieldNotes: "Notlar",
  intakeSubmit: "Ürün brief alımını çalıştır",
  intakeSubmitting: "Taslak oluşturuluyor…",
  intakeReset: "Sıfırla",
  intakeReportProductBrief: "Ürün brief özeti",
  intakeReportBrandContext: "Marka bağlamı özeti",
  intakeReportObjectiveSummary: "Ambalaj hedefi özeti",
  intakeReportMarketChannelFit: "Pazar ve kanal uyum notları",
  intakeReportMissingChecklist: "Eksik bilgi kontrol listesi",
  intakeReportAssumptionLog: "Varsayım günlüğü",
  intakeReportInitialRiskFlags: "İlk risk işaretleri",
  intakeReportHumanReview: "İnceleme gerektiren notlar",
  intakeReportRecommendedNextEngines: "Önerilen sonraki motorlar",

  intakeReportStrategy: "Ambalaj strateji görüntüsü",
  intakeReportTargetCustomer: "Hedef müşteri yorumu",
  intakeReportPositioning: "Konumlandırma notları",
  intakeReportChannelReadiness: "Kanal hazırlığı notları",
  intakeReportLocalization: "Dil ve yerelleştirme notları",
  intakeReportDecisionPriorities: "Ambalaj karar öncelikleri",
  intakeReportOwnerQuestions: "Sahip soruları",
  intakeNextLabelCopy: "Sonraki: Etiket ve iddia incelemesi",

  labelCopyTitle: "Etiket ve iddia incelemesi",
  labelCopySubtitle:
    "Belirleyici iddia-risk taraması + etiket metin iskeleti. Riskli iddialar onaylanmış olarak yeniden yazılmaz. Motor düzenleyici alıntı vermez.",
  labelCopyFieldProductName: "Ürün adı",
  labelCopyFieldCategory: "Ürün kategorisi",
  labelCopyFieldBrandName: "Marka adı",
  labelCopyFieldTargetMarket: "Hedef pazar (ISO-2)",
  labelCopyFieldTargetLanguage: "Hedef dil",
  labelCopyFieldSalesChannel: "Satış kanalı",
  labelCopyFieldProductDescription: "Ürün açıklaması",
  labelCopyFieldPackagingObjective: "Ambalaj hedefi",
  labelCopyFieldClaimDrafts: "İddia/metin taslakları",
  labelCopyFieldClaimDraftsHelp:
    "Satır başına bir iddia. En fazla 32, her biri 200 karakter.",
  labelCopyFieldToneOfVoice: "Ses tonu",
  labelCopyFieldClaimsToAvoid: "Kaçınılacak iddialar",
  labelCopyFieldRequiredLabelElements: "Zorunlu etiket öğeleri",
  labelCopyFieldRequiredLabelElementsHelp:
    "Satır başına bir öğe. En fazla 12.",
  labelCopySubmit: "Etiket ve iddia incelemesini çalıştır",
  labelCopySubmitting: "Taslak oluşturuluyor…",
  labelCopyReset: "Sıfırla",
  labelCopyReportFront: "Ön etiket taslağı",
  labelCopyReportBack: "Arka etiket taslağı",
  labelCopyReportStory: "Kısa ürün hikâyesi",
  labelCopyReportInstructions: "Kullanım/servis/saklama",
  labelCopyReportWarning: "Uyarı / dikkat",
  labelCopyReportClaimRiskTable: "İddia risk tablosu",
  labelCopyReportSaferWording: "Daha güvenli ifade önerileri",
  labelCopyReportMissingInfo: "Eksik etiket bilgisi",
  labelCopyReportHumanReview: "İnceleme gerektirir",
  labelCopyReportCopyQuality: "Metin kalitesi notları",
  labelCopyClaimColClaim: "İddia",
  labelCopyClaimColSeverity: "Şiddet",
  labelCopyClaimColCategory: "Kategori",
  labelCopyClaimColMatched: "Eşleşen terimler",
  labelCopyClaimColReview: "İnceleme",

  materialTitle: "Malzeme ve yapı",
  materialSubtitle:
    "Belirleyici ambalaj formatı / malzeme / yapı önerisi. Mühendislik veya üretim garantisi yok. İnceleme gereklidir.",
  materialFieldFragility: "Kırılganlık",
  materialFieldTemperatureSensitive: "Sıcaklığa duyarlı",
  materialFieldSustainabilityPreferences: "Sürdürülebilirlik tercihleri",
  materialReportFormat: "Önerilen ambalaj formatı",
  materialReportMaterial: "Malzeme önerisi",
  materialReportStructure: "Yapı önerisi",
  materialReportDurability: "Dayanıklılık / saklama",
  materialReportDelivery: "Teslimat uygunluğu",
  materialReportEcommerce: "E-ticaret uygunluğu",
  materialReportShelf: "Raf uygunluğu",
  materialReportProduction: "Üretim fizibilitesi",
  materialReportCost: "Maliyet hassasiyeti",
  materialReportSupplierQuestions: "Tedarikçi için açık sorular",
  materialSubmit: "Malzeme ve yapıyı çalıştır",
  materialSubmitting: "Taslak oluşturuluyor…",
  materialReset: "Sıfırla",
  intakeNextMaterial: "Sonraki: Malzeme ve yapı",

  sustTitle: "Sürdürülebilirlik ve uyumluluk riski",
  sustSubtitle:
    "Belirleyici yeşil aklama / sürdürülebilirlik / uyumluluk taraması. Motor düzenleyici alıntı vermez ve hiçbir iddiayı onaylamaz.",
  sustFieldPackagingMaterial: "Ambalaj malzemesi",
  sustFieldClaims: "Sürdürülebilirlik iddiaları",
  sustFieldClaimsHelp: "Satır başına bir iddia. En fazla 32, her biri 200 karakter.",
  sustFieldLabelDrafts: "Etiket/metin taslakları (isteğe bağlı)",
  sustFieldRecyclable: "Geri dönüştürülebilir iddiası var",
  sustFieldBiodegradable: "Biyobozunur iddiası var",
  sustFieldCompostable: "Kompostlanabilir iddiası var",
  sustFieldOrigin: "Menşe / made-in iddiası var",
  sustFieldBarcode: "Etikette barkod var",
  sustFieldExpiry: "Son kullanma / best-before var",
  sustFieldOriginLabel: "Menşe ülke etiketi var",
  sustFieldWarning: "Zorunlu uyarılar var",
  sustReportClaimReview: "Sürdürülebilirlik iddiası incelemesi",
  sustReportGreenwashing: "Yeşil aklama risk işaretleri",
  sustReportSeverityTable: "İddia şiddeti tablosu",
  sustReportSafer: "Daha güvenli sürdürülebilirlik ifadeleri",
  sustReportCompliance: "Uyumluluk hatırlatma listesi",
  sustReportCountry: "Ülke / bölge notları",
  sustReportEvidence: "Gerekli kanıt",
  sustReportNotApproved: "TASLAK — onaylanmadı",
  sustSubmit: "Sürdürülebilirlik ve uyumluluk riskini çalıştır",
  sustSubmitting: "Taslak oluşturuluyor…",
  sustReset: "Sıfırla",
  sustColTerm: "Terim",
  sustColSeverity: "Şiddet",
  sustColCategory: "Kategori",
  sustColEvidence: "Kanıt gerekli",
  labelCopyNextSust: "Sonraki: Sürdürülebilirlik ve uyumluluk riski",

  supplierTitle: "Tedarikçi brif / RFQ",
  supplierSubtitle:
    "Belirleyici tedarikçi brifi ve RFQ taslağı. Motor ASLA göndermez, tedarikçi seçmez ve nihai fiyat veya üretim onayı iddia etmez.",
  supplierFieldFormat: "Ambalaj formatı",
  supplierFieldMaterial: "Ambalaj malzemesi",
  supplierFieldStructure: "Yapı önerisi",
  supplierFieldQuantity: "Miktar aralığı",
  supplierFieldPrintFinish: "Baskı / son işlem",
  supplierFieldKnownConstraints: "Bilinen kısıtlar",
  supplierFieldQuestions: "Tedarikçi için sorular",
  supplierFieldQuestionsHelp: "Satır başına bir tane. En fazla 12.",
  supplierReportBrief: "Tedarikçi brif özeti",
  supplierReportSpec: "Ambalaj şartname taslağı",
  supplierReportMaterial: "Malzeme / yapı gereksinimleri",
  supplierReportPrint: "Baskı / son işlem gereksinimleri",
  supplierReportQuantity: "Miktar / MOQ soruları",
  supplierReportLead: "Teslim süresi soruları",
  supplierReportQuality: "Kalite / test soruları",
  supplierReportSust: "Sürdürülebilirlik / kanıt soruları",
  supplierReportRfqEmail: "RFQ e-posta taslağı (motor göndermez)",
  supplierReportCompare: "Tedarikçi karşılaştırma kriterleri",
  supplierReportMissing: "Eksik üretim bilgisi",
  supplierSubmit: "Tedarikçi brif / RFQ çalıştır",
  supplierSubmitting: "Taslak oluşturuluyor…",
  supplierReset: "Sıfırla",
  materialNextSupplier: "Sonraki: Tedarikçi brif / RFQ",
  sustNextSupplier: "Sonraki: Tedarikçi brif / RFQ (kanıt iste)",

  visualTitle: "Görsel yön",
  visualSubtitle:
    "Belirleyici görsel yön brifi. Motor ASLA görsel üretmez, rakip sanat eserini kopyalamaz ve nihai sanatı onaylamaz.",
  visualFieldBrandPersonality: "Marka kişiliği",
  visualFieldTargetCustomer: "Hedef müşteri",
  visualFieldVisualPreference: "Görsel tercih",
  visualFieldColorsToUse: "Kullanılacak renkler",
  visualFieldColorsToAvoid: "Kaçınılacak renkler",
  visualFieldTypographyPreference: "Tipografi tercihi",
  visualFieldCompetitorReferences: "Rakip referansları",
  visualFieldCompetitorReferencesHelp:
    "Satır başına bir tane. Yalnız ilham — asla kopyalanmaz.",
  visualReportSummary: "Görsel yön özeti",
  visualReportRoutes: "Tasarım rotası seçenekleri",
  visualReportColor: "Renk yönü",
  visualReportTypography: "Tipografi yönü",
  visualReportLayout: "Düzen yönü",
  visualReportSurface: "Ambalaj yüzey önceliği",
  visualReportShelfEc: "Raf / e-ticaret görsel notları",
  visualReportCultural: "Kültürel / yerelleştirme notları",
  visualReportMockup: "Mockup prompt paketi",
  visualReportHandoff: "Tasarımcı teslim notları",
  visualReportRisks: "Görsel riskler / kaçınma",
  visualSubmit: "Görsel yönü çalıştır",
  visualSubmitting: "Taslak oluşturuluyor…",
  visualReset: "Sıfırla",
  intakeNextVisual: "Sonraki: Görsel yön",
  labelCopyNextVisual: "Sonraki: Görsel yön",

  scorecardTitle: "Ambalaj puan kartı ve dikey paket",
  scorecardSubtitle:
    "Belirleyici 0–100 alt-puanlar (raf / e-ticaret / teslimat / kutu açma / pazaryeri), seçili dikey paketle. Görsel analiz yok. İnceleme gereklidir.",
  scorecardFieldVerticalPack: "Dikey paket",
  scorecardFieldShelfEnv: "Raf ortamı",
  scorecardFieldFragility: "Kırılganlık",
  scorecardFieldLiquid: "Sıvı",
  scorecardFieldTempSensitive: "Sıcaklığa duyarlı",
  scorecardFieldThumbnailOpt: "E-ticaret thumbnail optimize",
  scorecardFieldProtectiveInsert: "Koruyucu iç dolgu var",
  scorecardFieldLeakSeal: "Sızdırmaz conta var",
  scorecardFieldDropTest: "Düşme testi geçti",
  scorecardFieldVisualNotes: "Görsel yön notları",
  scorecardOverall: "Genel ambalaj puanı",
  scorecardSectionShelf: "Raf görünürlüğü",
  scorecardSectionEcommerce: "Thumbnail hazırlığı",
  scorecardSectionDelivery: "Teslimat dayanıklılığı",
  scorecardSectionUnboxing: "Kutu açma deneyimi",
  scorecardSectionMarketplace: "Pazaryeri hazırlığı",
  scorecardVerticalPackLabel: "Dikey paket",
  scorecardRiskFlags: "Risk işaretleri",
  scorecardImprovements: "İyileştirme önerileri",
  scorecardChannelRecs: "Kanala özel öneriler",
  scorecardMissing: "Eksik bilgi",
  scorecardSubmit: "Ambalaj puan kartını çalıştır",
  scorecardSubmitting: "Taslak oluşturuluyor…",
  scorecardReset: "Sıfırla",
  scorecardBandHigh: "yüksek",
  scorecardBandMedium: "orta",
  scorecardBandLow: "düşük",
  visualNextScorecard: "Sonraki: Ambalaj puan kartı",
  supplierNextScorecard: "Sonraki: Ambalaj puan kartı",
  launchKitTitle: "Lansman kiti",
  launchKitSubtitle: "Belirleyici lansman kopya TASLAKLARI; insan incelemesi için. Motor ASLA paylaşmaz ve nihai onay iddia etmez.",
  launchKitFieldPositioning: "Ambalaj konumlandırma",
  launchKitFieldKeyBenefit: "Ana fayda",
  launchKitFieldClaimRestrictions: "Kısıtlı iddialar",
  launchKitFieldQrConnected: "QR / bağlı ambalaj",
  launchKitFieldLaunchChannels: "Lansman kanalları",
  launchKitReportPositioning: "Lansman konumlandırma özeti",
  launchKitReportListing: "E-ticaret liste taslağı",
  launchKitReportSocial: "Sosyal medya içerik taslakları",
  launchKitReportInsert: "Insert kart metni",
  launchKitReportQr: "QR / bağlı fikir",
  launchKitReportLoyalty: "İnceleme / yeniden sipariş / sadakat metni",
  launchKitReportSafety: "İddia güvenlik notları",
  launchKitSubmit: "Lansman kitini çalıştır",
  launchKitSubmitting: "Taslak oluşturuluyor…",
  launchKitReset: "Sıfırla",
  postLaunchTitle: "Lansman sonrası analiz",
  postLaunchSubtitle: "Yalnızca operatör tarafından yapıştırılan veri. Harici scraping yok, marketplace API yok, PII yok, istatistiksel iddia yok.",
  postLaunchFieldReviews: "İnceleme parçaları (satır başına bir)",
  postLaunchFieldReturns: "İade nedenleri (satır başına bir)",
  postLaunchFieldFeedback: "Müşteri geri bildirimi (satır başına bir)",
  postLaunchFieldPerformance: "E-ticaret performans notları",
  postLaunchFieldVersion: "Ambalaj sürümü",
  postLaunchReportThemes: "Geri bildirim tema özeti",
  postLaunchReportComplaints: "Ambalaj şikayet sınıflandırması",
  postLaunchReportReturns: "İade / hasar sınıflandırması",
  postLaunchReportSentiment: "İnceleme tonu notları",
  postLaunchReportImprovements: "Ambalaj iyileştirme önerileri",
  postLaunchReportNext: "Sonraki yineleme öncelikleri",
  postLaunchReportDataQuality: "Veri kalitesi / eksik veri",
  postLaunchSubmit: "Lansman sonrası analizi çalıştır",
  postLaunchSubmitting: "Taslak oluşturuluyor…",
  postLaunchReset: "Sıfırla",
  reviewChecklistTitle: "İnceleme kontrol listesi",
  reviewChecklistSubtitle: "Belirleyici kontrol listesi + risk inceleme şablonu + ihracat hazırlığı. DB yok, kalıcılık yok, nihai onay yok.",
  reviewChecklistFieldTarget: "İş akışı hedefi",
  reviewChecklistFieldHighRisk: "Yüksek riskli iddia mevcut",
  reviewChecklistFieldEvidenceGap: "Kanıt boşluğu mevcut",
  reviewChecklistFieldSustClaim: "Desteklenmeyen sürdürülebilirlik iddiası",
  reviewChecklistFieldFoodContact: "Gıda teması endişesi",
  reviewChecklistFieldCosmetic: "Kozmetik güvenlik endişesi",
  reviewChecklistFieldIndustrial: "Endüstriyel tehlike endişesi",
  reviewChecklistFieldSupplier: "Tedarikçi belirsizliği",
  reviewChecklistReportChecklist: "İnceleme kontrol listesi",
  reviewChecklistReportCriteria: "Onay kriterleri",
  reviewChecklistReportRisks: "Risk inceleme maddeleri",
  reviewChecklistReportEvidence: "Gerekli kanıt",
  reviewChecklistReportExport: "İhracat hazırlığı",
  reviewChecklistReportDecision: "İncelemeci karar şablonu",
  reviewChecklistSubmit: "İnceleme kontrol listesini çalıştır",
  reviewChecklistSubmitting: "Taslak oluşturuluyor…",
  reviewChecklistReset: "Sıfırla",
  servicesTitle: "Hizmetler ve ambalaj zekâsı paketleri",
  servicesIntro:
    "Packaging OS, ekiplerin tasarım, baskı, tedarikçi, iddia veya lansmana para harcamadan önce daha iyi ambalaj kararları almasına yardımcı olur. Her çıktı, karar desteği sağlayan bir taslaktır.",
  servicesHumanReview: "Her çıktı için insan incelemesi zorunludur",
  servicesNotLegal: "Yasal veya düzenleyici onay değildir",
  servicesNoBilling: "Çevrimiçi ödeme yok — süreç bir taleple başlar",
  packPilotReady: "Pilot başvuruları açık",
  packFoundation: "Ticari temel",
  packKnownGaps: "Bilinen eksikler",
  packWorkflows: "Dahil iş akışları",
  packRequestQuote: "Fiyat teklifi isteyin",
  pricingTitle: "Paketler ve fiyatlandırma",
  pricingIntro:
    "Fiyatlar her iş birliği için ayrıca belirlenir. Çevrimiçi ödeme veya abonelik yoktur — talep gönderin, ekibimiz sizinle iletişime geçsin.",
  pricingDraftLabel: "Taslak fiyatlandırma — teklif isteyin",
  requestTitle: "Erişim, demo, denetim veya pilot talebi",
  requestIntro:
    "Ürününüzü ve pazarınızı anlatın. Talepler kaydedilir ve bir insan tarafından incelenir — otomatik e-posta gönderilmez.",
  requestTypeLabel: "Neye ihtiyacınız var?",
  requestTypeAccess: "Platform erişimi",
  requestTypeDemo: "Demo",
  requestTypeAudit: "Ambalaj denetimi",
  requestTypePilot: "Pilot proje",
  requestNameLabel: "Adınız",
  requestEmailLabel: "E-posta",
  requestCompanyLabel: "Şirket / marka",
  requestCountryLabel: "Ülke / pazar",
  requestLanguageLabel: "Tercih edilen dil",
  requestMessageLabel: "Mesaj (isteğe bağlı)",
  requestSubmit: "Talebi gönder",
  requestSubmitting: "Gönderiliyor…",
  requestSuccess: "Talebiniz kaydedildi. Ekip inceleyip sizinle iletişime geçecek.",
  requestNoEmailNote: "Bu aşamada otomatik e-posta gönderilmez.",
  requestValidationError: "Lütfen geçerli bir ad ve e-posta girin.",
  adminTitle: "Yönetici konsolu",
  adminOverviewTab: "Genel bakış",
  adminReviewOpsTab: "İnceleme operasyonları",
  adminCommercialTab: "Ticari",
  adminSupportTab: "Destek",
  adminBlockedMessage: "Bu konsol sahip/yönetici izni gerektirir.",
  adminCardUsers: "Kullanıcılar",
  adminCardRuns: "İş akışı çalıştırmaları",
  adminCardReview: "İnceleme bekleyen belgeler",
  adminCardGaps: "Açık kaynak boşlukları",
  adminCardKnowledge: "Bilgi dosyaları",
  adminCardCost: "Toplam maliyet (USD)",
  adminCardRequests: "Yeni ticari talepler",
  adminAiModeLabel: "Yapay zekâ modu",
  adminBillingDisabled: "Faturalandırma kapalı — ödeme sağlayıcısı yok",
  runSavedBanner: "Çalıştırma kaydedildi — taslak belge oluşturuldu (insan incelemesi gerekli)",
  runSavedArtifactLink: "Belgeyi aç",
  navArtifacts: "Belgeler ve dışa aktarımlar",
  navKnowledge: "Bilgi",
  navAdmin: "Yönetici konsolu",
  artifactsTitle: "Belgeler ve dışa aktarımlar",
  artifactsIntro: "Kaydedilen her çalıştırma bir taslak belge oluşturur. Burada inceleyin, onaylayın ve dışa aktarın — nihai dışa aktarım onaya kadar kilitli kalır.",
  artifactStatusLabel: "Durum",
  artifactReviewApprove: "Onayla",
  artifactReviewReject: "Reddet",
  artifactExportDraft: "Taslak dışa aktarım (MD)",
  artifactExportFinal: "Nihai dışa aktarım (PDF)",
  artifactExportFinalGated: "Nihai dışa aktarım onaylanmış belge gerektirir.",
  artifactDownload: "İndir",
  artifactSourceGaps: "Açık kaynak boşlukları",
  artifactEvidence: "Bağlantılı kanıtlar",
  artifactVersions: "Sürümler",
  knowledgeTitle: "Bilgi beyni",
  knowledgeIntro: "Referans belgeler yükleyin (PDF/DOCX/TXT/CSV/XLSX). Yüklenen içerik gerçek değildir — çıkarım uyarıları görünür kalır ve kanıtlar insan doğrulaması gerektirir.",
  knowledgeUploadLabel: "Belge dosyası",
  knowledgeLanguageHint: "Kaynak dili (beyan edilen)",
  knowledgeUploadButton: "Yükle",
  knowledgeExtractButton: "Çıkar",
  knowledgeSearchLabel: "Çalışma alanı bilgisinde ara",
  knowledgeSearchButton: "Ara",
  knowledgeWarnings: "Çıkarım uyarıları",
  knowledgeNoOcrNote: "Taranmış/görüntü PDF desteklenmez (OCR yok) — dürüstçe uyarıyla başarısız olur.",
};

const ALL: Record<LocaleCode, UiStrings> = { en, ar, fa, tr };

export function strings(locale: LocaleCode): UiStrings {
  return ALL[locale] ?? ALL.en;
}
